import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row, Card, CardHeader, CardBody, Button, Spinner, FormGroup, Label, Input } from 'reactstrap';
import { useColorPalette } from '../hooks/useColorPalette';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPaperclip, faFile, faFileImage, faFilePdf, faFileWord, faFileExcel, faFilePowerpoint, faFileLines, faFileZipper, faFileAudio, faFileVideo, faDownload, faChevronDown, faEye, faList, faClock, faToolbox, faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { selectAccountData } from '../redux/main/accountSlice';
import { getUsersList, selectUsersData } from '../redux/users/usersSlice';
import { getCustomersList, selectCustomersData } from '../redux/customers/customersSlice';
import { getJobsList, selectJobsData } from '../redux/jobs/jobsSlice';
import moment from 'moment';
import Select2 from '../components/Select2';
import CustomerSelect from '../components/CustomerSelect';
import { EmployeeSelect } from '../components/EmployeeSelect';
import JobSelect from '../components/JobSelect';
import DynamicTitle from '../components/DynamicTitle';
import EmptyState from '../components/DataTable/EmptyState';
import { getNodeApiUrl } from '../config/api.config';
import axios from 'axios';
import AttachmentsViewer from '../components/AttachmentsViewer';

// Registrare i componenti Chart.js necessari
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function SummaryHours() {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token');
    const colors = useColorPalette();
    const users = useSelector(selectUsersData);
    const customers = useSelector(selectCustomersData);
    const jobs = useSelector(selectJobsData);

    // Stati per i filtri - inizializzati con il mese corrente completo
    const [startDate, setStartDate] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().endOf('month').format('YYYY-MM-DD'));
    const [employeeId, setEmployeeId] = useState('');
    const [employeeLabel, setEmployeeLabel] = useState('');
    const [customerId, setCustomerId] = useState('');
    const [customerLabel, setCustomerLabel] = useState('');
    const [jobId, setJobId] = useState('');
    const [jobLabel, setJobLabel] = useState('');
    
    // Stati per i dati
    const [appointments, setAppointments] = useState([]);
    const [absences, setAbsences] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedEmployee, setExpandedEmployee] = useState(null);
    const [viewMode, setViewMode] = useState('cards'); // 'cards' o 'table'

    // Stato per tracciare il download di file in corso
    const [downloadingFiles, setDownloadingFiles] = useState({});

    // Funzione per il caricamento degli appuntamenti e delle assenze con i filtri
    const fetchAppointments = async () => {
        if (!accountData?._id) return;

        setLoading(true);
        try {
            // Carica appuntamenti
            const appointmentsUrl = getNodeApiUrl('/appointments/findByFilters');
            const appointmentsResponse = await axios.post(appointmentsUrl, {
                accountId: accountData._id,
                startDate: moment(startDate).startOf('day').toISOString(),
                endDate: moment(endDate).endOf('day').toISOString(),
                // Non inviare employeeId al backend, lo filtreremo lato client
                // così potremo filtrare i closingDetails specifici
                customerId: customerId || undefined,
                jobId: jobId || undefined
            }, {
                headers: { 'auth-token': token }
            });
            
            // Tengo tutti gli appuntamenti, anche quelli senza closingDetails:
            // i collaboratori assegnati che non hanno inserito le ore vengono
            // comunque elencati a ZERO (vedi hoursSummary)
            setAppointments(Array.isArray(appointmentsResponse.data) ? appointmentsResponse.data : []);
            
            // Carica assenze (ferie, permessi, malattia)
            const absencesUrl = getNodeApiUrl('/employeeAbsences');
            const absencesResponse = await axios.get(absencesUrl, {
                params: {
                    accountId: accountData._id,
                    startDate: moment(startDate).startOf('day').toISOString(),
                    endDate: moment(endDate).endOf('day').toISOString(),
                    userId: employeeId || undefined
                },
                headers: { 'auth-token': token }
            });
            
            setAbsences(absencesResponse.data?.data || []);
        } catch (error) {
            console.error('Errore nel recupero dei dati:', error);
        } finally {
            setLoading(false);
        }
    };

    // Caricamento iniziale dei dati necessari
    useEffect(() => {
        if (accountData?._id) {
            dispatch(getUsersList({ accountId: accountData._id, token }));
            dispatch(getCustomersList({ accountId: accountData._id, token, search: '', start: 0, length: 1000, prospect: 'all' }));
            dispatch(getJobsList({ 
                accountId: accountData._id, 
                token,
                start: 0,
                length: 1000
            }));
        }
    }, [accountData?._id, dispatch, token]);

    // Caricamento degli appuntamenti quando cambia un filtro
    useEffect(() => {
        if (accountData?._id) {
            fetchAppointments();
        }
    }, [accountData?._id, startDate, endDate, employeeId, customerId, jobId]);

    // Funzione helper per calcolare le ore di un'assenza nel periodo selezionato
    const calculateAbsenceHours = (absence, periodStart, periodEnd) => {
        const absStart = moment(absence.start);
        const absEnd = moment(absence.end);
        const pStart = periodStart ? moment(periodStart).startOf('day') : absStart;
        const pEnd = periodEnd ? moment(periodEnd).endOf('day') : absEnd;
        
        // Se l'assenza è completamente fuori dal periodo, ritorna 0
        if (absEnd.isBefore(pStart) || absStart.isAfter(pEnd)) {
            return 0;
        }
        
        // Se l'assenza inizia e finisce nello stesso giorno, calcola le ore effettive
        if (absStart.isSame(absEnd, 'day')) {
            const dayOfWeek = absStart.day();
            // Solo giorni lavorativi (lun-ven)
            if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                // Verifica che il giorno sia nel periodo selezionato
                if (absStart.isBetween(pStart, pEnd, 'day', '[]')) {
                    // Calcola la differenza in ore tra start e end
                    const hours = absEnd.diff(absStart, 'hours', true);
                    return Math.round(hours * 10) / 10; // Arrotonda a 1 decimale
                }
            }
            return 0;
        }
        
        // Assenza su più giorni: calcola le ore per ogni giorno lavorativo nel periodo
        let totalHours = 0;
        let current = absStart.clone().startOf('day');
        
        while (current.isSameOrBefore(absEnd, 'day')) {
            const dayOfWeek = current.day();
            // Solo giorni lavorativi (lun-ven) e nel periodo selezionato
            if (dayOfWeek >= 1 && dayOfWeek <= 5 && current.isBetween(pStart, pEnd, 'day', '[]')) {
                // Per il primo giorno, calcola dalle ore di inizio alle 16:00 (fine giornata)
                if (current.isSame(absStart, 'day')) {
                    const dayEnd = current.clone().hour(16).minute(0);
                    const hours = dayEnd.diff(absStart, 'hours', true);
                    totalHours += Math.max(0, Math.min(8, hours));
                }
                // Per l'ultimo giorno, calcola dalle 08:00 (inizio giornata) alle ore di fine
                else if (current.isSame(absEnd, 'day')) {
                    const dayStart = current.clone().hour(8).minute(0);
                    const hours = absEnd.diff(dayStart, 'hours', true);
                    totalHours += Math.max(0, Math.min(8, hours));
                }
                // Per i giorni intermedi, conta 8 ore
                else {
                    totalHours += 8;
                }
            }
            current.add(1, 'day');
        }
        
        return Math.round(totalHours * 10) / 10; // Arrotonda a 1 decimale
    };

    // Festività italiane (formato MM-DD per festività fisse, calcolate per Pasqua)
    const getItalianHolidays = (year) => {
        const holidays = [
            `${year}-01-01`, // Capodanno
            `${year}-01-06`, // Epifania
            `${year}-04-25`, // Liberazione
            `${year}-05-01`, // Festa dei lavoratori
            `${year}-06-02`, // Festa della Repubblica
            `${year}-08-15`, // Ferragosto
            `${year}-11-01`, // Tutti i Santi
            `${year}-12-08`, // Immacolata
            `${year}-12-25`, // Natale
            `${year}-12-26`, // Santo Stefano
        ];

        // Calcolo Pasqua (algoritmo di Gauss)
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;

        const easter = moment({ year, month: month - 1, day });
        holidays.push(easter.format('YYYY-MM-DD')); // Pasqua
        holidays.push(easter.clone().add(1, 'day').format('YYYY-MM-DD')); // Pasquetta

        return holidays;
    };

    // Verifica se un giorno è festivo (sabato, domenica o festività italiana)
    const isHoliday = (dayToCheck) => {
        const dayOfWeek = dayToCheck.day();
        if (dayOfWeek === 0 || dayOfWeek === 6) return true; // Domenica o Sabato

        const year = dayToCheck.year();
        const holidays = getItalianHolidays(year);
        return holidays.includes(dayToCheck.format('YYYY-MM-DD'));
    };

    // Calcolo del riepilogo delle ore
    const hoursSummary = useMemo(() => {
        // Aggregazione delle ore per employee
        const employeeHours = {};
        let totalHours = 0;
        let totalMinutes = 0;
        let totalOvertimeHours = 0;
        let totalOvertimeMinutes = 0;

        // 1. Elabora gli appuntamenti (ore lavorative)
        const endOfToday = moment().endOf('day');

        appointments.forEach(appointment => {
            const closingDetails = Array.isArray(appointment.closingDetails) ? appointment.closingDetails : [];

            // Righe di consuntivo: le chiusure reali + i collaboratori assegnati
            // all'appuntamento che NON hanno inserito le ore (segnati a ZERO).
            // Le ore mancanti si considerano solo per appuntamenti già trascorsi,
            // altrimenti quelli ancora da svolgere risulterebbero tutti a zero.
            const details = [...closingDetails];
            if (moment(appointment.fromDate).isSameOrBefore(endOfToday)) {
                (appointment.users || []).forEach(user => {
                    if (!user?.userId) return;
                    if (closingDetails.some(detail => detail.userId === user.userId)) return;
                    details.push({
                        userId: user.userId,
                        userLabel: user.userLabel,
                        workHours: 0,
                        workMinutes: 0,
                        overtimeHours: 0,
                        overtimeMinutes: 0,
                        missingHours: true
                    });
                });
            }

            // Filtro i dettagli in base all'employeeId selezionato
            const filteredDetails = details.filter(detail => {
                if (employeeId && employeeId !== '') {
                    return detail.userId === employeeId;
                }
                return true;
            });

            filteredDetails.forEach(detail => {
                // Verifica se l'appuntamento cade in un giorno festivo (sab, dom, festivi italiani)
                const appointmentDay = moment(appointment.fromDate);
                const isHolidayDay = isHoliday(appointmentDay);

                // Aggregazione per employee
                const empId = detail.userId;
                if (!employeeHours[empId]) {
                    employeeHours[empId] = {
                        userId: empId,
                        userLabel: detail.userLabel || 'Collaboratore sconosciuto',
                        totalHours: 0,
                        totalMinutes: 0,
                        totalOvertimeHours: 0,
                        totalOvertimeMinutes: 0,
                        hoursByType: {
                            lavorate: { hours: 0, minutes: 0 },
                            ferie: { hours: 0, minutes: 0 },
                            assenze: { hours: 0, minutes: 0 },
                            malattia: { hours: 0, minutes: 0 },
                            altro: { hours: 0, minutes: 0 }
                        },
                        missingHoursCount: 0,
                        appointments: [],
                        absences: []
                    };
                }

                // Conteggio delle chiusure mancanti (ore non inserite dal collaboratore)
                if (detail.missingHours) {
                    employeeHours[empId].missingHoursCount += 1;
                }

                if (isHolidayDay) {
                    // Giorni festivi: tutte le ore lavorate → straordinario
                    const holidayOvertimeH = (detail.workHours || 0) + (detail.overtimeHours || 0);
                    const holidayOvertimeM = (detail.workMinutes || 0) + (detail.overtimeMinutes || 0);
                    employeeHours[empId].totalOvertimeHours += holidayOvertimeH;
                    employeeHours[empId].totalOvertimeMinutes += holidayOvertimeM;
                    // Aggiungi anche al totale globale straordinario
                    totalOvertimeHours += holidayOvertimeH;
                    totalOvertimeMinutes += holidayOvertimeM;
                } else {
                    // Giorno lavorativo: logica normale
                    totalHours += detail.workHours || 0;
                    totalMinutes += detail.workMinutes || 0;

                    employeeHours[empId].totalHours += detail.workHours || 0;
                    employeeHours[empId].totalMinutes += detail.workMinutes || 0;

                    employeeHours[empId].totalOvertimeHours += detail.overtimeHours || 0;
                    employeeHours[empId].totalOvertimeMinutes += detail.overtimeMinutes || 0;
                    totalOvertimeHours += detail.overtimeHours || 0;
                    totalOvertimeMinutes += detail.overtimeMinutes || 0;

                    employeeHours[empId].hoursByType.lavorate.hours += detail.workHours || 0;
                    employeeHours[empId].hoursByType.lavorate.minutes += detail.workMinutes || 0;
                }

                // Converto minuti in eccesso in ore per ogni tipo
                Object.keys(employeeHours[empId].hoursByType).forEach(type => {
                    const typeData = employeeHours[empId].hoursByType[type];
                    if (typeData.minutes >= 60) {
                        typeData.hours += Math.floor(typeData.minutes / 60);
                        typeData.minutes = typeData.minutes % 60;
                    }
                });

                // Converto minuti in eccesso in ore totali
                if (employeeHours[empId].totalMinutes >= 60) {
                    employeeHours[empId].totalHours += Math.floor(employeeHours[empId].totalMinutes / 60);
                    employeeHours[empId].totalMinutes = employeeHours[empId].totalMinutes % 60;
                }

                // Converto minuti straordinario in eccesso
                if (employeeHours[empId].totalOvertimeMinutes >= 60) {
                    employeeHours[empId].totalOvertimeHours += Math.floor(employeeHours[empId].totalOvertimeMinutes / 60);
                    employeeHours[empId].totalOvertimeMinutes = employeeHours[empId].totalOvertimeMinutes % 60;
                }

                // Aggiungo l'appuntamento alla lista dell'employee
                employeeHours[empId].appointments.push({
                    ...appointment,
                    closingDetail: detail
                });
            });
        });

        // Normalizza totali globali
        if (totalMinutes >= 60) {
            totalHours += Math.floor(totalMinutes / 60);
            totalMinutes = totalMinutes % 60;
        }
        if (totalOvertimeMinutes >= 60) {
            totalOvertimeHours += Math.floor(totalOvertimeMinutes / 60);
            totalOvertimeMinutes = totalOvertimeMinutes % 60;
        }

        // 2. Elabora le assenze (ferie, permessi, malattia, formazione)
        absences.forEach(absence => {
            if (employeeId && employeeId !== '' && absence.userId !== employeeId) {
                return;
            }

            const empId = absence.userId;

            if (!employeeHours[empId]) {
                employeeHours[empId] = {
                    userId: empId,
                    userLabel: absence.userLabel || 'Collaboratore sconosciuto',
                    totalHours: 0,
                    totalMinutes: 0,
                    totalOvertimeHours: 0,
                    totalOvertimeMinutes: 0,
                    hoursByType: {
                        lavorate: { hours: 0, minutes: 0 },
                        ferie: { hours: 0, minutes: 0 },
                        assenze: { hours: 0, minutes: 0 },
                        malattia: { hours: 0, minutes: 0 },
                        altro: { hours: 0, minutes: 0 }
                    },
                    missingHoursCount: 0,
                    appointments: [],
                    absences: []
                };
            }

            // Calcola le ore dell'assenza nel periodo selezionato
            const absenceHoursCalc = calculateAbsenceHours(absence, startDate, endDate);

            // Mappa il tipo di assenza al tipo corretto
            const absenceTypeLower = (absence.absenceType || '').toLowerCase();
            let typeKey = 'assenze'; // Default: assenze generiche
            if (absenceTypeLower.includes('formazione')) {
                typeKey = 'lavorate'; // FORMAZIONE conta come ore lavorative
            } else if (absenceTypeLower.includes('ferie') || absenceTypeLower.includes('feria')) {
                typeKey = 'ferie';
            } else if (absenceTypeLower.includes('malattia')) {
                typeKey = 'malattia';
            }

            // Aggiungi le ore al tipo corretto
            employeeHours[empId].hoursByType[typeKey].hours += absenceHoursCalc;

            // Aggiungi le ore al totale dell'employee
            employeeHours[empId].totalHours += absenceHoursCalc;

            // Aggiungi anche al totale globale
            totalHours += absenceHoursCalc;

            // Aggiungi l'assenza alla lista
            employeeHours[empId].absences.push({
                ...absence,
                calculatedHours: absenceHoursCalc,
                typeKey
            });
        });

        // Post-processing: ricalcola ore lavorate e straordinari con logica per giorno
        // (overflow >8h, festivi, permesso parziale + lavoro)
        Object.values(employeeHours).forEach(emp => {
            // 1. Raggruppa appuntamenti per giorno
            const appointmentsByDay = {};
            emp.appointments.forEach(app => {
                const dateKey = moment(app.fromDate).startOf('day').format('YYYY-MM-DD');
                if (!appointmentsByDay[dateKey]) appointmentsByDay[dateKey] = [];
                appointmentsByDay[dateKey].push(app.closingDetail);
            });

            // 2. Calcola assenze per giorno (dal state globale absences)
            const absencesByDay = {};
            const empAbsences = absences.filter(abs => abs.userId === emp.userId);

            empAbsences.forEach(absence => {
                const absStart = moment(absence.start);
                const absEnd = moment(absence.end);
                const pStart = moment(startDate).startOf('day');
                const pEnd = moment(endDate).endOf('day');
                const absenceTypeLower = (absence.absenceType || '').toLowerCase();
                const isFormazione = absenceTypeLower.includes('formazione');

                if (absStart.isSame(absEnd, 'day')) {
                    // Assenza same-day
                    const dayOfWeek = absStart.day();
                    if (dayOfWeek >= 1 && dayOfWeek <= 5 && absStart.isBetween(pStart, pEnd, 'day', '[]')) {
                        const dateKey = absStart.format('YYYY-MM-DD');
                        if (!absencesByDay[dateKey]) absencesByDay[dateKey] = [];
                        const hours = absEnd.diff(absStart, 'hours', true);
                        absencesByDay[dateKey].push({ hoursForDay: Math.round(hours * 10) / 10, isFormazione });
                    }
                } else {
                    // Assenza multi-giorno
                    let curr = absStart.clone().startOf('day');
                    while (curr.isSameOrBefore(absEnd, 'day')) {
                        if (curr.isBefore(pStart, 'day') || curr.isAfter(pEnd, 'day')) { curr.add(1, 'day'); continue; }
                        const dayOfWeek = curr.day();
                        if (dayOfWeek >= 1 && dayOfWeek <= 5) {
                            const dateKey = curr.format('YYYY-MM-DD');
                            if (!absencesByDay[dateKey]) absencesByDay[dateKey] = [];
                            let hoursForDay = 8;
                            if (curr.isSame(absStart, 'day')) {
                                const dayEnd = curr.clone().hour(16).minute(0);
                                hoursForDay = Math.max(0, Math.min(8, dayEnd.diff(absStart, 'hours', true)));
                            } else if (curr.isSame(absEnd, 'day')) {
                                const dayStart = curr.clone().hour(8).minute(0);
                                hoursForDay = Math.max(0, Math.min(8, absEnd.diff(dayStart, 'hours', true)));
                            }
                            absencesByDay[dateKey].push({ hoursForDay: Math.round(hoursForDay * 10) / 10, isFormazione });
                        }
                        curr.add(1, 'day');
                    }
                }
            });

            // 3. Per ogni giorno con attività, calcola ore regolari e straordinari
            let correctLavorateMin = 0;
            let correctOvertimeMin = 0;
            const allDays = new Set([...Object.keys(appointmentsByDay), ...Object.keys(absencesByDay)]);

            allDays.forEach(dateKey => {
                const day = moment(dateKey);
                const holidayFlag = isHoliday(day);

                // Ore da appuntamenti
                let dayWorkMin = 0;
                let dayExplOvertimeMin = 0;
                (appointmentsByDay[dateKey] || []).forEach(detail => {
                    dayWorkMin += (detail.workHours || 0) * 60 + (detail.workMinutes || 0);
                    dayExplOvertimeMin += (detail.overtimeHours || 0) * 60 + (detail.overtimeMinutes || 0);
                });

                // Ore assenze per questo giorno
                let dayAbsenceMin = 0; // non-FORMAZIONE
                let dayFormazioneMin = 0;
                (absencesByDay[dateKey] || []).forEach(entry => {
                    const mins = Math.round(entry.hoursForDay * 60);
                    if (entry.isFormazione) {
                        dayFormazioneMin += mins;
                    } else {
                        dayAbsenceMin += mins;
                    }
                });

                // Totale lavoro del giorno = appuntamenti + FORMAZIONE
                const totalWorkMin = dayWorkMin + dayFormazioneMin;

                if (holidayFlag) {
                    // Festivo: tutto il lavoro → straordinario
                    correctOvertimeMin += totalWorkMin + dayExplOvertimeMin;
                } else {
                    // Giorno lavorativo: soglia = 8h - assenze non-FORMAZIONE
                    const capMin = Math.max(0, 8 * 60 - dayAbsenceMin);
                    if (totalWorkMin > capMin) {
                        correctLavorateMin += capMin;
                        correctOvertimeMin += (totalWorkMin - capMin) + dayExplOvertimeMin;
                    } else {
                        correctLavorateMin += totalWorkMin;
                        correctOvertimeMin += dayExplOvertimeMin;
                    }
                }
            });

            // 4. Aggiorna i totali dell'employee
            emp.hoursByType.lavorate = {
                hours: Math.floor(correctLavorateMin / 60),
                minutes: correctLavorateMin % 60
            };
            emp.totalOvertimeHours = Math.floor(correctOvertimeMin / 60);
            emp.totalOvertimeMinutes = correctOvertimeMin % 60;

            // Ricalcola totalHours = somma di tutti i tipi (lavorate + ferie + assenze + malattia + altro)
            let tMin = 0;
            Object.values(emp.hoursByType).forEach(v => {
                tMin += (v.hours || 0) * 60 + (v.minutes || 0);
            });
            // hoursByType.ferie/assenze/malattia possono avere ore decimali (es. 8.5)
            emp.totalHours = Math.floor(tMin / 60);
            emp.totalMinutes = Math.round(tMin % 60);
        });

        // Ricalcola totali globali dai dati per employee
        totalHours = 0;
        totalMinutes = 0;
        totalOvertimeHours = 0;
        totalOvertimeMinutes = 0;

        Object.values(employeeHours).forEach(emp => {
            totalHours += emp.totalHours;
            totalMinutes += emp.totalMinutes;
            totalOvertimeHours += emp.totalOvertimeHours;
            totalOvertimeMinutes += emp.totalOvertimeMinutes;
        });

        if (totalMinutes >= 60) {
            totalHours += Math.floor(totalMinutes / 60);
            totalMinutes = totalMinutes % 60;
        }
        if (totalOvertimeMinutes >= 60) {
            totalOvertimeHours += Math.floor(totalOvertimeMinutes / 60);
            totalOvertimeMinutes = totalOvertimeMinutes % 60;
        }

        // Converto l'oggetto in array e ordino alfabeticamente per nome collaboratore
        const employeeSummary = Object.values(employeeHours).sort((a, b) => {
            return (a.userLabel || '').localeCompare(b.userLabel || '');
        });

        if (employeeSummary.length === 0) {
            return {
                totalHours: 0,
                totalMinutes: 0,
                totalOvertimeHours: 0,
                totalOvertimeMinutes: 0,
                employeeSummary: []
            };
        }

        return {
            totalHours,
            totalMinutes,
            totalOvertimeHours,
            totalOvertimeMinutes,
            employeeSummary
        };
    }, [appointments, absences, employeeId, startDate, endDate]);

    // Gestione dell'espansione dei dettagli dell'employee
    const toggleEmployeeDetails = (employeeId) => {
        if (expandedEmployee === employeeId) {
            setExpandedEmployee(null);
        } else {
            setExpandedEmployee(employeeId);
        }
    };

    // Formattazione delle ore
    const formatHours = (hours, minutes) => {
        if (!hours && !minutes) return '0h';
        if (!hours) return `${minutes}m`;
        if (!minutes) return `${hours}h`;
        return `${hours}h ${minutes}m`;
    };

    // Ottieni iniziale del giorno della settimana in italiano
    const getDayInitial = (day) => {
        const dayOfWeek = day.day();
        const initials = ['D', 'L', 'M', 'M', 'G', 'V', 'S']; // Dom, Lun, Mar, Mer, Gio, Ven, Sab
        return initials[dayOfWeek];
    };

    // Funzione per generare i dati della tabella per un collaboratore (usata sia per stampa che per visualizzazione a video)
    const generateEmployeeTableData = (employee) => {
        // Mappa degli appuntamenti per data - usa startOf('day') per normalizzare timezone
        const appointmentsByDate = {};
        employee.appointments.forEach(appointment => {
            const dateKey = moment(appointment.fromDate).startOf('day').format('YYYY-MM-DD');
            if (!appointmentsByDate[dateKey]) {
                appointmentsByDate[dateKey] = [];
            }
            appointmentsByDate[dateKey].push(appointment);
        });
        
        // Mappa delle assenze per data - usa lo state globale absences filtrato per questo collaboratore
        const absencesByDate = {};
        const employeeAbsences = absences.filter(abs => abs.userId === employee.userId);
        employeeAbsences.forEach(absence => {
            // Usa startOf('day') per ignorare l'orario e confrontare solo le date
            const absStart = moment(absence.start).startOf('day');
            const absEnd = moment(absence.end).startOf('day');
            let curr = absStart.clone();
            
            // Limita al periodo selezionato
            const periodStart = moment(startDate).startOf('day');
            const periodEnd = moment(endDate).startOf('day');
            
            while (curr.isSameOrBefore(absEnd, 'day')) {
                // Verifica che il giorno sia nel periodo selezionato
                if (curr.isBefore(periodStart, 'day') || curr.isAfter(periodEnd, 'day')) {
                    curr.add(1, 'day');
                    continue;
                }
                
                // Includi tutti i giorni con assenze (anche weekend)
                const dateKey = curr.format('YYYY-MM-DD');
                if (!absencesByDate[dateKey]) {
                    absencesByDate[dateKey] = [];
                }
                absencesByDate[dateKey].push(absence);
                curr.add(1, 'day');
            }
        });
        
        // Genera la lista dei giorni con attività (appuntamenti o assenze)
        const allDatesWithActivity = new Set([
            ...Object.keys(appointmentsByDate),
            ...Object.keys(absencesByDate)
        ]);
        const sortedDates = Array.from(allDatesWithActivity).sort();
        const activeDays = sortedDates.map(dateKey => moment(dateKey));
        
        // Preparazione delle righe della tabella per giorni con attività
        return activeDays.map(day => {
            const dateKey = day.format('YYYY-MM-DD');
            const dayInitial = getDayInitial(day);
            const dateFormatted = `${dayInitial} ${day.format('DD/MM/YYYY')}`;
            const holidayFlag = isHoliday(day);
            const appointments = appointmentsByDate[dateKey] || [];
            const dayAbsences = absencesByDate[dateKey] || [];
            
            let workHours = 0;
            let workMinutes = 0;
            let absenceHours = 0;
            let absenceMinutes = 0;
            let overtimeHours = 0;
            let overtimeMinutes = 0;
            let workType = '';
            let descriptions = [];
            let hasMissingHours = false;
            let absenceDetails = []; // Array per i dettagli delle assenze

            // Elabora appuntamenti
            if (appointments.length > 0) {
                appointments.forEach(appointment => {
                    const detail = appointment.closingDetail;
                    if (detail) {
                        workHours += detail.workHours || 0;
                        workMinutes += detail.workMinutes || 0;
                        overtimeHours += detail.overtimeHours || 0;
                        overtimeMinutes += detail.overtimeMinutes || 0;

                        if (detail.missingHours) {
                            hasMissingHours = true;
                        } else if (!workType) {
                            workType = 'Lavorate';
                        }

                        // Mostra sempre e solo i riferimenti della commessa
                        if (appointment.jobCode || appointment.customerLabel) {
                            let descParts = [];
                            if (appointment.jobCode) descParts.push(appointment.jobCode);
                            if (appointment.customerLabel) descParts.push(appointment.customerLabel);
                            descriptions.push(descParts.join(' - ') + (detail.missingHours ? ' (ore non inserite)' : ''));
                        }
                    }
                });
            }

            // Elabora assenze - separa FORMAZIONE (conta come ore lavorative) dalle altre
            if (dayAbsences.length > 0) {
                dayAbsences.forEach(absence => {
                    const absenceTypeLower = (absence.absenceType || '').toLowerCase();
                    const isFormazione = absenceTypeLower.includes('formazione');

                    // Calcola le ore effettive dell'assenza per questo giorno
                    const absStart = moment(absence.start);
                    const absEnd = moment(absence.end);
                    let absenceHoursForDay = 8; // Default 8h per giorno intero

                    // Se l'assenza inizia e finisce nello stesso giorno, calcola ore effettive
                    if (absStart.isSame(absEnd, 'day')) {
                        absenceHoursForDay = absEnd.diff(absStart, 'hours', true);
                    } else {
                        // Per assenze multi-giorno, calcola le ore per questo specifico giorno
                        const currentDay = moment(day);
                        if (currentDay.isSame(absStart, 'day')) {
                            const dayEnd = currentDay.clone().hour(16).minute(0);
                            absenceHoursForDay = Math.max(0, Math.min(8, dayEnd.diff(absStart, 'hours', true)));
                        } else if (currentDay.isSame(absEnd, 'day')) {
                            const dayStart = currentDay.clone().hour(8).minute(0);
                            absenceHoursForDay = Math.max(0, Math.min(8, absEnd.diff(dayStart, 'hours', true)));
                        }
                    }

                    // Converti in ore e minuti
                    const wholeHours = Math.floor(absenceHoursForDay);
                    const remainingMinutes = Math.round((absenceHoursForDay - wholeHours) * 60);

                    if (isFormazione) {
                        // FORMAZIONE → conta come ore lavorative
                        workHours += wholeHours;
                        workMinutes += remainingMinutes;
                    } else {
                        // Altre assenze → conta come assenze
                        absenceHours += wholeHours;
                        absenceMinutes += remainingMinutes;
                    }

                    let absenceLabel = 'Assenza';
                    if (isFormazione) {
                        absenceLabel = 'Formazione';
                    } else if (absenceTypeLower.includes('ferie') || absenceTypeLower.includes('feria')) {
                        absenceLabel = 'Ferie';
                    } else if (absenceTypeLower.includes('malattia')) {
                        absenceLabel = 'Malattia';
                    }

                    if (!workType) {
                        workType = isFormazione ? 'Lavorate' : absenceLabel;
                    } else if (workType !== absenceLabel && workType !== 'Misto') {
                        workType = 'Misto';
                    }

                    // Calcola intervallo orario per questo giorno
                    let timeStart, timeEnd;
                    if (absStart.isSame(absEnd, 'day')) {
                        timeStart = absStart.format('HH:mm');
                        timeEnd = absEnd.format('HH:mm');
                    } else {
                        const currentDay = moment(day);
                        if (currentDay.isSame(absStart, 'day')) {
                            timeStart = absStart.format('HH:mm');
                            timeEnd = '16:00';
                        } else if (currentDay.isSame(absEnd, 'day')) {
                            timeStart = '08:00';
                            timeEnd = absEnd.format('HH:mm');
                        } else {
                            timeStart = '08:00';
                            timeEnd = '16:00';
                        }
                    }

                    if (isFormazione) {
                        // FORMAZIONE: mostra nella descrizione come ore lavorative
                        const hoursLabel = absenceHoursForDay < 8 ? ` (${absenceHoursForDay.toFixed(1)}h)` : '';
                        descriptions.push(`Formazione${hoursLabel}`);
                    } else {
                        // Altre assenze: mostra nei dettagli assenze
                        absenceDetails.push(`${absence.absenceType || 'Assenza'} ${timeStart}-${timeEnd}`);
                        const hoursLabel = absenceHoursForDay < 8 ? ` (${absenceHoursForDay.toFixed(1)}h)` : '';
                        descriptions.push(`${absence.absenceType || 'Assenza'}${hoursLabel}`);
                    }
                });
            }

            // Normalizza i minuti per ore lavorate
            if (workMinutes >= 60) {
                workHours += Math.floor(workMinutes / 60);
                workMinutes = workMinutes % 60;
            }
            // Normalizza i minuti per ore assenza
            if (absenceMinutes >= 60) {
                absenceHours += Math.floor(absenceMinutes / 60);
                absenceMinutes = absenceMinutes % 60;
            }
            if (overtimeMinutes >= 60) {
                overtimeHours += Math.floor(overtimeMinutes / 60);
                overtimeMinutes = overtimeMinutes % 60;
            }

            // Calcolo straordinario in base al tipo di giorno
            let displayWorkHours = workHours;
            let displayWorkMinutes = workMinutes;

            if (holidayFlag) {
                // Giorno festivo (sab, dom, festivi italiani): tutte le ore lavorate → straordinario
                overtimeHours += workHours;
                overtimeMinutes += workMinutes;
                displayWorkHours = 0;
                displayWorkMinutes = 0;
                if (overtimeMinutes >= 60) {
                    overtimeHours += Math.floor(overtimeMinutes / 60);
                    overtimeMinutes = overtimeMinutes % 60;
                }
            } else {
                // Giorno lavorativo: soglia straordinario = 8h - ore assenze (non FORMAZIONE)
                const totalWorkInMinutes = workHours * 60 + workMinutes;
                const totalAbsenceInMinutes = absenceHours * 60 + absenceMinutes;
                const regularCapMinutes = Math.max(0, 8 * 60 - totalAbsenceInMinutes);

                if (totalWorkInMinutes > regularCapMinutes) {
                    const excessMinutes = totalWorkInMinutes - regularCapMinutes;
                    displayWorkHours = Math.floor(regularCapMinutes / 60);
                    displayWorkMinutes = regularCapMinutes % 60;
                    overtimeHours += Math.floor(excessMinutes / 60);
                    overtimeMinutes += excessMinutes % 60;
                    if (overtimeMinutes >= 60) {
                        overtimeHours += Math.floor(overtimeMinutes / 60);
                        overtimeMinutes = overtimeMinutes % 60;
                    }
                }
            }

            // Costruisci stringa assenze con label e intervallo
            let absencesDisplay = '-';
            if (absenceHours > 0 || absenceMinutes > 0) {
                const totalAbsenceTime = formatHours(absenceHours, absenceMinutes);
                if (absenceDetails.length > 0) {
                    absencesDisplay = `${totalAbsenceTime}<br/><small>${absenceDetails.join('; ')}</small>`;
                } else {
                    absencesDisplay = totalAbsenceTime;
                }
            }

            return {
                date: dateFormatted,
                isHoliday: holidayFlag,
                description: descriptions.length > 0 ? descriptions.join('; ') : '-',
                workType: workType || (hasMissingHours ? 'Da inserire' : '-'),
                hasMissingHours,
                hours: displayWorkHours > 0 || displayWorkMinutes > 0 ? formatHours(displayWorkHours, displayWorkMinutes) : '-',
                absences: absencesDisplay,
                overtime: overtimeHours > 0 || overtimeMinutes > 0 ? formatHours(overtimeHours, overtimeMinutes) : '-',
                isEmpty: displayWorkHours === 0 && displayWorkMinutes === 0 && absenceHours === 0 && absenceMinutes === 0 && overtimeHours === 0 && overtimeMinutes === 0
            };
        });
    };

    // Funzione per stampare il foglio ore completo di tutti i collaboratori
    const generateAllEmployeeTimesheets = () => {
        try {
            if (!hoursSummary?.employeeSummary || hoursSummary.employeeSummary.length === 0) {
                alert('Non ci sono dati disponibili per il periodo selezionato');
                return;
            }
            
            const formattedStartDate = moment(startDate).format('DD/MM/YYYY');
            const formattedEndDate = moment(endDate).format('DD/MM/YYYY');
            const title = `Riepilogo ore collaboratori`;
            const subtitle = `Periodo: ${formattedStartDate} - ${formattedEndDate}`;
            
            // Recupero logo dell'account (stesso della sidebar)
            const accountLogo = accountData?.settings?.UI?.logo || '';
            
            // Costruzione dell'HTML
            let htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>${title}</title>
                    <style>
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        
                        body { 
                            font-family: Arial, sans-serif; 
                            background-color: white;
                            color: #000;
                            padding: 20px;
                            font-size: 11px;
                        }
                        
                        .page {
                            page-break-after: always;
                        }
                        
                        .page:last-child {
                            page-break-after: auto;
                        }
                        
                        .logo-container { 
                            text-align: center; 
                            margin-bottom: 20px; 
                        }
                        
                        .logo { 
                            max-height: 60px; 
                            max-width: 200px;
                        }
                        
                        h1 { 
                            color: #000; 
                            font-size: 18px;
                            font-weight: bold;
                            text-align: center;
                            margin: 10px 0 5px 0;
                        }
                        
                        .subtitle {
                            text-align: center;
                            font-size: 12px;
                            color: #666;
                            margin-bottom: 15px;
                        }
                        
                        .summary { 
                            display: flex;
                            justify-content: center;
                            gap: 20px;
                            flex-wrap: wrap;
                            margin-bottom: 15px;
                            padding: 10px;
                            background-color: #f9f9f9;
                            border-radius: 4px;
                        }
                        
                        .summary-item { 
                            font-size: 12px;
                            font-weight: 600;
                            color: #000;
                        }
                        
                        .summary-item span {
                            background-color: #e0e0e0;
                            padding: 2px 8px;
                            border-radius: 3px;
                            margin-left: 5px;
                        }
                        
                        table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            margin-top: 10px; 
                        }
                        
                        th, td { 
                            border: 1px solid #000; 
                            padding: 6px 8px; 
                            text-align: left;
                            color: #000;
                        }
                        
                        th { 
                            background-color: #f5f5f5;
                            font-weight: bold;
                            font-size: 10px;
                        }
                        
                        td {
                            font-size: 10px;
                        }
                        
                        .day-name {
                            font-weight: 500;
                            color: #333;
                        }
                        
                        .empty-day {
                            color: #999;
                        }
                        
                        .holiday-row {
                            background-color: #f5f5f5;
                        }

                        .missing-hours-row {
                            background-color: #fff3cd;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                            color: #000;
                        }
                        
                        .totals-row {
                            background-color: #f0f0f0;
                            font-weight: bold;
                        }
                        
                        .grand-total-row {
                            background-color: #d0d0d0;
                            font-weight: bold;
                        }
                        
                        /* Stili per la stampa */
                        @media print {
                            body {
                                padding: 10px;
                                margin: 0;
                                background-color: white;
                            }
                            
                            .page {
                                page-break-after: always;
                                padding: 10px;
                            }
                            
                            .page:last-child {
                                page-break-after: auto;
                            }
                            
                            th {
                                background-color: #f5f5f5 !important;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            
                            .totals-row {
                                background-color: #f0f0f0 !important;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            
                            .grand-total-row {
                                background-color: #d0d0d0 !important;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            
                            table {
                                page-break-inside: auto;
                            }
                            
                            tr {
                                page-break-inside: avoid;
                                page-break-after: auto;
                            }
                        }
                    </style>
                </head>
                <body>
            `;
            
            // ========== PAGINA 1: RIEPILOGO GENERALE ==========
            // Calcola il totale generale di tutti i collaboratori
            let grandTotalHours = 0;
            let grandTotalMinutes = 0;
            let grandTotalOvertimeHours = 0;
            let grandTotalOvertimeMinutes = 0;
            
            // Totali per tipo (ore lavorate, ferie, permessi, malattia)
            const grandTotalsByType = {
                lavorate: { hours: 0, minutes: 0 },
                ferie: { hours: 0, minutes: 0 },
                assenze: { hours: 0, minutes: 0 },
                malattia: { hours: 0, minutes: 0 }
            };
            
            hoursSummary.employeeSummary.forEach(emp => {
                grandTotalHours += emp.totalHours || 0;
                grandTotalMinutes += emp.totalMinutes || 0;
                grandTotalOvertimeHours += emp.totalOvertimeHours || 0;
                grandTotalOvertimeMinutes += emp.totalOvertimeMinutes || 0;
                
                // Somma i totali per tipo
                if (emp.hoursByType) {
                    Object.keys(grandTotalsByType).forEach(type => {
                        if (emp.hoursByType[type]) {
                            grandTotalsByType[type].hours += emp.hoursByType[type].hours || 0;
                            grandTotalsByType[type].minutes += emp.hoursByType[type].minutes || 0;
                        }
                    });
                }
            });
            
            // Normalizza i minuti per ogni tipo
            Object.keys(grandTotalsByType).forEach(type => {
                if (grandTotalsByType[type].minutes >= 60) {
                    grandTotalsByType[type].hours += Math.floor(grandTotalsByType[type].minutes / 60);
                    grandTotalsByType[type].minutes = grandTotalsByType[type].minutes % 60;
                }
            });
            
            // Normalizza i minuti totali
            if (grandTotalMinutes >= 60) {
                grandTotalHours += Math.floor(grandTotalMinutes / 60);
                grandTotalMinutes = grandTotalMinutes % 60;
            }
            if (grandTotalOvertimeMinutes >= 60) {
                grandTotalOvertimeHours += Math.floor(grandTotalOvertimeMinutes / 60);
                grandTotalOvertimeMinutes = grandTotalOvertimeMinutes % 60;
            }
            
            htmlContent += `
                <div class="page">
                    ${accountLogo ? `
                    <div class="logo-container">
                        <img src="${accountLogo}" class="logo" alt="Logo"/>
                    </div>
                    ` : ''}
                    
                    <h1>${title}</h1>
                    <div class="subtitle">${subtitle}</div>
                    
                    <div class="summary">
                        <div class="summary-item">Totale Generale: <span>${formatHours(grandTotalHours, grandTotalMinutes)}</span></div>
                        ${(grandTotalOvertimeHours > 0 || grandTotalOvertimeMinutes > 0) ? `
                            <div class="summary-item">Straordinario Totale: <span>${formatHours(grandTotalOvertimeHours, grandTotalOvertimeMinutes)}</span></div>
                        ` : ''}
                    </div>
                    
                    <table>
                        <thead>
                            <tr>
                                <th>Collaboratore</th>
                                <th style="width: 100px;">Ore Lavorate</th>
                                <th style="width: 80px;">Ferie</th>
                                <th style="width: 80px;">Assenze</th>
                                <th style="width: 80px;">Malattia</th>
                                <th style="width: 80px;">Straordinario</th>
                                <th style="width: 100px;">Totale</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${hoursSummary.employeeSummary.map(emp => {
                                const hoursByType = emp.hoursByType || {};
                                return `
                                    <tr>
                                        <td><strong>${emp.userLabel}</strong></td>
                                        <td>${hoursByType.lavorate ? formatHours(hoursByType.lavorate.hours, hoursByType.lavorate.minutes) : '-'}</td>
                                        <td>${hoursByType.ferie ? formatHours(hoursByType.ferie.hours, hoursByType.ferie.minutes) : '-'}</td>
                                        <td>${hoursByType.assenze ? formatHours(hoursByType.assenze.hours, hoursByType.assenze.minutes) : '-'}</td>
                                        <td>${hoursByType.malattia ? formatHours(hoursByType.malattia.hours, hoursByType.malattia.minutes) : '-'}</td>
                                        <td>${(emp.totalOvertimeHours > 0 || emp.totalOvertimeMinutes > 0) ? formatHours(emp.totalOvertimeHours, emp.totalOvertimeMinutes) : '-'}</td>
                                        <td><strong>${formatHours(emp.totalHours, emp.totalMinutes)}</strong></td>
                                    </tr>
                                `;
                            }).join('')}
                            <tr class="grand-total-row">
                                <td style="text-align: right;"><strong>TOTALE GENERALE</strong></td>
                                <td><strong>${formatHours(grandTotalsByType.lavorate.hours, grandTotalsByType.lavorate.minutes)}</strong></td>
                                <td><strong>${formatHours(grandTotalsByType.ferie.hours, grandTotalsByType.ferie.minutes)}</strong></td>
                                <td><strong>${formatHours(grandTotalsByType.assenze.hours, grandTotalsByType.assenze.minutes)}</strong></td>
                                <td><strong>${formatHours(grandTotalsByType.malattia.hours, grandTotalsByType.malattia.minutes)}</strong></td>
                                <td><strong>${formatHours(grandTotalOvertimeHours, grandTotalOvertimeMinutes)}</strong></td>
                                <td><strong>${formatHours(grandTotalHours, grandTotalMinutes)}</strong></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            `;
            
            // ========== PAGINE DETTAGLIO PER OGNI COLLABORATORE ==========
            hoursSummary.employeeSummary.forEach((employee) => {
                // Mappa degli appuntamenti per data - usa startOf('day') per normalizzare timezone
                const appointmentsByDate = {};
                employee.appointments.forEach(appointment => {
                    const dateKey = moment(appointment.fromDate).startOf('day').format('YYYY-MM-DD');
                    if (!appointmentsByDate[dateKey]) {
                        appointmentsByDate[dateKey] = [];
                    }
                    appointmentsByDate[dateKey].push(appointment);
                });
                
                // Mappa delle assenze per data - usa lo state globale absences filtrato per questo collaboratore
                const absencesByDate = {};
                const employeeAbsences = absences.filter(abs => abs.userId === employee.userId);
                employeeAbsences.forEach(absence => {
                    // Usa startOf('day') per ignorare l'orario e confrontare solo le date
                    const absStart = moment(absence.start).startOf('day');
                    const absEnd = moment(absence.end).startOf('day');
                    let curr = absStart.clone();
                    
                    // Limita al periodo selezionato
                    const periodStart = moment(startDate).startOf('day');
                    const periodEnd = moment(endDate).startOf('day');
                    
                    while (curr.isSameOrBefore(absEnd, 'day')) {
                        // Verifica che il giorno sia nel periodo selezionato
                        if (curr.isBefore(periodStart, 'day') || curr.isAfter(periodEnd, 'day')) {
                            curr.add(1, 'day');
                            continue;
                        }
                        
                        // Includi tutti i giorni con assenze (anche weekend)
                        const dateKey = curr.format('YYYY-MM-DD');
                        if (!absencesByDate[dateKey]) {
                            absencesByDate[dateKey] = [];
                        }
                        // Passa l'assenza originale con le date complete per il calcolo delle ore
                        absencesByDate[dateKey].push(absence);
                        curr.add(1, 'day');
                    }
                });
                
                // Genera la lista dei giorni con attività (appuntamenti o assenze)
                // invece di usare solo i giorni lun-ven teorici
                const allDatesWithActivity = new Set([
                    ...Object.keys(appointmentsByDate),
                    ...Object.keys(absencesByDate)
                ]);
                const sortedDates = Array.from(allDatesWithActivity).sort();
                const activeDays = sortedDates.map(dateKey => moment(dateKey));
                
                // Variabili per calcolare i totali effettivi delle colonne
                let totalWorkHours = 0;
                let totalWorkMinutes = 0;
                let totalAbsenceHours = 0;
                let totalAbsenceMinutes = 0;
                let totalOvertimeHours = 0;
                let totalOvertimeMinutes = 0;
                
                // Preparazione delle righe della tabella per ogni giorno con attività
                const dayRows = activeDays.map(day => {
                    const dateKey = day.format('YYYY-MM-DD');
                    const dayInitial = getDayInitial(day);
                    const dateFormatted = `${dayInitial} ${day.format('DD/MM/YYYY')}`;
                    const holidayFlag = isHoliday(day);
                    const appointments = appointmentsByDate[dateKey] || [];
                    const dayAbsences = absencesByDate[dateKey] || [];
                    
                    let workHours = 0;
                    let workMinutes = 0;
                    let absenceHours = 0;
                    let absenceMinutes = 0;
                    let overtimeHours = 0;
                    let overtimeMinutes = 0;
                    let workType = '';
                    let descriptions = [];
                    let hasMissingHours = false;
                    let absenceDetails = [];

                    // Elabora appuntamenti (ore lavorative)
                    if (appointments.length > 0) {
                        appointments.forEach(appointment => {
                            const detail = appointment.closingDetail;
                            if (detail) {
                                workHours += detail.workHours || 0;
                                workMinutes += detail.workMinutes || 0;
                                overtimeHours += detail.overtimeHours || 0;
                                overtimeMinutes += detail.overtimeMinutes || 0;

                                if (detail.missingHours) {
                                    hasMissingHours = true;
                                } else if (!workType) {
                                    workType = 'Lavorate';
                                }

                                if (appointment.jobCode || appointment.customerLabel) {
                                    let descParts = [];
                                    if (appointment.jobCode) descParts.push(appointment.jobCode);
                                    if (appointment.customerLabel) descParts.push(appointment.customerLabel);
                                    descriptions.push(descParts.join(' - ') + (detail.missingHours ? ' (ore non inserite)' : ''));
                                }
                            }
                        });
                    }

                    // Elabora assenze - separa FORMAZIONE dalle altre
                    if (dayAbsences.length > 0) {
                        dayAbsences.forEach(absence => {
                            const absenceTypeLower = (absence.absenceType || '').toLowerCase();
                            const isFormazione = absenceTypeLower.includes('formazione');

                            const absStart = moment(absence.start);
                            const absEnd = moment(absence.end);
                            let absenceHoursForDay = 8;

                            if (absStart.isSame(absEnd, 'day')) {
                                absenceHoursForDay = absEnd.diff(absStart, 'hours', true);
                            } else {
                                const currentDay = moment(day);
                                if (currentDay.isSame(absStart, 'day')) {
                                    const dayEnd = currentDay.clone().hour(16).minute(0);
                                    absenceHoursForDay = Math.max(0, Math.min(8, dayEnd.diff(absStart, 'hours', true)));
                                } else if (currentDay.isSame(absEnd, 'day')) {
                                    const dayStart = currentDay.clone().hour(8).minute(0);
                                    absenceHoursForDay = Math.max(0, Math.min(8, absEnd.diff(dayStart, 'hours', true)));
                                }
                            }

                            const wholeHours = Math.floor(absenceHoursForDay);
                            const remainingMinutes = Math.round((absenceHoursForDay - wholeHours) * 60);

                            if (isFormazione) {
                                workHours += wholeHours;
                                workMinutes += remainingMinutes;
                            } else {
                                absenceHours += wholeHours;
                                absenceMinutes += remainingMinutes;
                            }

                            let absenceLabel = 'Assenza';
                            if (isFormazione) {
                                absenceLabel = 'Formazione';
                            } else if (absenceTypeLower.includes('ferie') || absenceTypeLower.includes('feria')) {
                                absenceLabel = 'Ferie';
                            } else if (absenceTypeLower.includes('malattia')) {
                                absenceLabel = 'Malattia';
                            }

                            if (!workType) {
                                workType = isFormazione ? 'Lavorate' : absenceLabel;
                            } else if (workType !== absenceLabel && workType !== 'Misto') {
                                workType = 'Misto';
                            }

                            let timeStart, timeEnd;
                            if (absStart.isSame(absEnd, 'day')) {
                                timeStart = absStart.format('HH:mm');
                                timeEnd = absEnd.format('HH:mm');
                            } else {
                                const currentDay = moment(day);
                                if (currentDay.isSame(absStart, 'day')) {
                                    timeStart = absStart.format('HH:mm');
                                    timeEnd = '16:00';
                                } else if (currentDay.isSame(absEnd, 'day')) {
                                    timeStart = '08:00';
                                    timeEnd = absEnd.format('HH:mm');
                                } else {
                                    timeStart = '08:00';
                                    timeEnd = '16:00';
                                }
                            }

                            if (isFormazione) {
                                const hoursLabel = absenceHoursForDay < 8 ? ` (${absenceHoursForDay.toFixed(1)}h)` : '';
                                descriptions.push(`Formazione${hoursLabel}`);
                            } else {
                                absenceDetails.push(`${absence.absenceType || 'Assenza'} ${timeStart}-${timeEnd}`);
                                const hoursLabel = absenceHoursForDay < 8 ? ` (${absenceHoursForDay.toFixed(1)}h)` : '';
                                descriptions.push(`${absence.absenceType || 'Assenza'}${hoursLabel}`);
                            }
                        });
                    }

                    // Normalizza i minuti
                    if (workMinutes >= 60) {
                        workHours += Math.floor(workMinutes / 60);
                        workMinutes = workMinutes % 60;
                    }
                    if (absenceMinutes >= 60) {
                        absenceHours += Math.floor(absenceMinutes / 60);
                        absenceMinutes = absenceMinutes % 60;
                    }
                    if (overtimeMinutes >= 60) {
                        overtimeHours += Math.floor(overtimeMinutes / 60);
                        overtimeMinutes = overtimeMinutes % 60;
                    }

                    // Calcolo straordinario
                    let displayWorkHours = workHours;
                    let displayWorkMinutes = workMinutes;

                    if (holidayFlag) {
                        // Giorno festivo: tutte le ore lavorate → straordinario
                        overtimeHours += workHours;
                        overtimeMinutes += workMinutes;
                        displayWorkHours = 0;
                        displayWorkMinutes = 0;
                        if (overtimeMinutes >= 60) {
                            overtimeHours += Math.floor(overtimeMinutes / 60);
                            overtimeMinutes = overtimeMinutes % 60;
                        }
                    } else {
                        // Giorno lavorativo: soglia = 8h - ore assenze
                        const totalWorkInMinutes = workHours * 60 + workMinutes;
                        const totalAbsenceInMinutes = absenceHours * 60 + absenceMinutes;
                        const regularCapMinutes = Math.max(0, 8 * 60 - totalAbsenceInMinutes);

                        if (totalWorkInMinutes > regularCapMinutes) {
                            const excessMinutes = totalWorkInMinutes - regularCapMinutes;
                            displayWorkHours = Math.floor(regularCapMinutes / 60);
                            displayWorkMinutes = regularCapMinutes % 60;
                            overtimeHours += Math.floor(excessMinutes / 60);
                            overtimeMinutes += excessMinutes % 60;
                            if (overtimeMinutes >= 60) {
                                overtimeHours += Math.floor(overtimeMinutes / 60);
                                overtimeMinutes = overtimeMinutes % 60;
                            }
                        }
                    }

                    // Costruisci stringa assenze
                    let absencesDisplay = '-';
                    if (absenceHours > 0 || absenceMinutes > 0) {
                        const totalAbsenceTime = formatHours(absenceHours, absenceMinutes);
                        if (absenceDetails.length > 0) {
                            absencesDisplay = `${totalAbsenceTime}<br/><small>${absenceDetails.join('; ')}</small>`;
                        } else {
                            absencesDisplay = totalAbsenceTime;
                        }
                    }

                    // Accumula i totali delle colonne
                    totalWorkHours += displayWorkHours;
                    totalWorkMinutes += displayWorkMinutes;
                    totalAbsenceHours += absenceHours;
                    totalAbsenceMinutes += absenceMinutes;
                    totalOvertimeHours += overtimeHours;
                    totalOvertimeMinutes += overtimeMinutes;

                    return {
                        date: dateFormatted,
                        isHoliday: holidayFlag,
                        description: descriptions.length > 0 ? descriptions.join('; ') : '-',
                        workType: workType || (hasMissingHours ? 'Da inserire' : '-'),
                        hasMissingHours,
                        hours: displayWorkHours > 0 || displayWorkMinutes > 0 ? formatHours(displayWorkHours, displayWorkMinutes) : '-',
                        absences: absencesDisplay,
                        overtime: overtimeHours > 0 || overtimeMinutes > 0 ? formatHours(overtimeHours, overtimeMinutes) : '-'
                    };
                });

                // Normalizza i minuti totali
                if (totalWorkMinutes >= 60) {
                    totalWorkHours += Math.floor(totalWorkMinutes / 60);
                    totalWorkMinutes = totalWorkMinutes % 60;
                }
                if (totalAbsenceMinutes >= 60) {
                    totalAbsenceHours += Math.floor(totalAbsenceMinutes / 60);
                    totalAbsenceMinutes = totalAbsenceMinutes % 60;
                }
                if (totalOvertimeMinutes >= 60) {
                    totalOvertimeHours += Math.floor(totalOvertimeMinutes / 60);
                    totalOvertimeMinutes = totalOvertimeMinutes % 60;
                }

                // Calcola i totali per tipo
                const totalsByType = {};
                Object.keys(employee.hoursByType || {}).forEach(type => {
                    const data = employee.hoursByType[type];
                    if (data.hours > 0 || data.minutes > 0) {
                        const typeLabels = {
                            lavorate: 'Lavorate',
                            ferie: 'Ferie',
                            assenze: 'Assenze',
                            malattia: 'Malattia',
                            altro: 'Altro'
                        };
                        totalsByType[typeLabels[type] || type] = formatHours(data.hours, data.minutes);
                    }
                });
                
                const employeeTitle = `Foglio ore di ${employee.userLabel}`;
                
                htmlContent += `
                    <div class="page">
                        ${accountLogo ? `
                        <div class="logo-container">
                            <img src="${accountLogo}" class="logo" alt="Logo"/>
                        </div>
                        ` : ''}
                        
                        <h1>${employeeTitle}</h1>
                        <div class="subtitle">${subtitle}</div>
                        
                        <div class="summary">
                            <div class="summary-item">Totale: <span>${formatHours(employee.totalHours, employee.totalMinutes)}</span></div>
                            ${Object.entries(totalsByType).map(([type, hours]) => `
                                <div class="summary-item">${type}: <span>${hours}</span></div>
                            `).join('')}
                            ${(employee.totalOvertimeHours > 0 || employee.totalOvertimeMinutes > 0) ? `
                                <div class="summary-item">Straordinario: <span>${formatHours(employee.totalOvertimeHours, employee.totalOvertimeMinutes)}</span></div>
                            ` : ''}
                            ${employee.missingHoursCount > 0 ? `
                                <div class="summary-item">Ore non inserite: <span>${employee.missingHoursCount}</span></div>
                            ` : ''}
                        </div>
                        
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 80px;">Data</th>
                                    <th>Descrizione</th>
                                    <th style="width: 80px;">Tipo Ore</th>
                                    <th style="width: 60px;">Ore</th>
                                    <th style="width: 60px;">Assenze</th>
                                    <th style="width: 70px;">Straord.</th>
                                </tr>
                            </thead>
                            <tbody>
                                ${dayRows.map(row => `
                                    <tr class="${row.isHoliday ? 'holiday-row' : ''} ${row.hasMissingHours ? 'missing-hours-row' : ''} ${!row.hasMissingHours && row.hours === '-' && row.absences === '-' ? 'empty-day' : ''}">
                                        <td>${row.date}</td>
                                        <td>${row.description}</td>
                                        <td>${row.workType}</td>
                                        <td>${row.hours}</td>
                                        <td>${row.absences}</td>
                                        <td>${row.overtime}</td>
                                    </tr>
                                `).join('')}
                                <tr class="totals-row">
                                    <td colspan="3" style="text-align: right;">TOTALE</td>
                                    <td>${formatHours(totalWorkHours, totalWorkMinutes)}</td>
                                    <td>${formatHours(totalAbsenceHours, totalAbsenceMinutes)}</td>
                                    <td>${formatHours(totalOvertimeHours, totalOvertimeMinutes)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                `;
            });
            
            // Chiudo l'HTML
            htmlContent += `
                    <script>
                        // Stampa automaticamente quando la pagina è caricata
                        window.onload = function() {
                            window.print();
                        }
                    </script>
                </body>
                </html>
            `;
            
            // Apri una nuova finestra con il contenuto da stampare
            const printWindow = window.open('', '_blank', 'width=800,height=600');
            if (printWindow) {
                printWindow.document.write(htmlContent);
                printWindow.document.close();
            }
            
        } catch (error) {
            console.error('Errore durante la stampa dei fogli ore:', error);
            alert('Si è verificato un errore durante la preparazione della stampa');
        }
    };
    
    // Funzione per stampare il foglio ore per un singolo collaboratore
    const generateEmployeeTimesheet = (employee) => {
        try {
            // Mappa degli appuntamenti per data - usa startOf('day') per normalizzare timezone
            const appointmentsByDate = {};
            employee.appointments.forEach(appointment => {
                const dateKey = moment(appointment.fromDate).startOf('day').format('YYYY-MM-DD');
                if (!appointmentsByDate[dateKey]) {
                    appointmentsByDate[dateKey] = [];
                }
                appointmentsByDate[dateKey].push(appointment);
            });
            
            // Mappa delle assenze per data - usa lo state globale absences filtrato per questo collaboratore
            const absencesByDate = {};
            const employeeAbsences = absences.filter(abs => abs.userId === employee.userId);
            employeeAbsences.forEach(absence => {
                // Usa startOf('day') per ignorare l'orario e confrontare solo le date
                const absStart = moment(absence.start).startOf('day');
                const absEnd = moment(absence.end).startOf('day');
                let current = absStart.clone();
                
                // Limita al periodo selezionato
                const periodStart = moment(startDate).startOf('day');
                const periodEnd = moment(endDate).startOf('day');
                
                while (current.isSameOrBefore(absEnd, 'day')) {
                    // Verifica che il giorno sia nel periodo selezionato
                    if (current.isBefore(periodStart, 'day') || current.isAfter(periodEnd, 'day')) {
                        current.add(1, 'day');
                        continue;
                    }
                    
                    // Includi tutti i giorni con assenze (anche weekend)
                    const dateKey = current.format('YYYY-MM-DD');
                    if (!absencesByDate[dateKey]) {
                        absencesByDate[dateKey] = [];
                    }
                    absencesByDate[dateKey].push(absence);
                    current.add(1, 'day');
                }
            });
            
            // Genera la lista dei giorni con attività (appuntamenti o assenze)
            const allDatesWithActivity = new Set([
                ...Object.keys(appointmentsByDate),
                ...Object.keys(absencesByDate)
            ]);
            const sortedDates = Array.from(allDatesWithActivity).sort();
            const activeDays = sortedDates.map(dateKey => moment(dateKey));
            
            // Variabili per calcolare i totali effettivi delle colonne
            let totalWorkHours = 0;
            let totalWorkMinutes = 0;
            let totalAbsenceHours = 0;
            let totalAbsenceMinutes = 0;
            let totalOvertimeHours = 0;
            let totalOvertimeMinutes = 0;
            
            // Preparazione delle righe della tabella per giorni con attività
            const dayRows = activeDays.map(day => {
                const dateKey = day.format('YYYY-MM-DD');
                const dayInitial = getDayInitial(day);
                const dateFormatted = `${dayInitial} ${day.format('DD/MM/YYYY')}`;
                const holidayFlag = isHoliday(day);
                const appointments = appointmentsByDate[dateKey] || [];
                const dayAbsences = absencesByDate[dateKey] || [];

                let workHours = 0;
                let workMinutes = 0;
                let absenceHours = 0;
                let absenceMinutes = 0;
                let overtimeHours = 0;
                let overtimeMinutes = 0;
                let workType = '';
                let descriptions = [];
                let hasMissingHours = false;
                let absenceDetails = [];

                // Elabora appuntamenti (ore lavorative)
                if (appointments.length > 0) {
                    appointments.forEach(appointment => {
                        const detail = appointment.closingDetail;
                        if (detail) {
                            workHours += detail.workHours || 0;
                            workMinutes += detail.workMinutes || 0;
                            overtimeHours += detail.overtimeHours || 0;
                            overtimeMinutes += detail.overtimeMinutes || 0;

                            if (detail.missingHours) {
                                hasMissingHours = true;
                            } else if (!workType) {
                                workType = 'Lavorate';
                            }

                            if (appointment.jobCode || appointment.customerLabel) {
                                let descParts = [];
                                if (appointment.jobCode) descParts.push(appointment.jobCode);
                                if (appointment.customerLabel) descParts.push(appointment.customerLabel);
                                descriptions.push(descParts.join(' - ') + (detail.missingHours ? ' (ore non inserite)' : ''));
                            }
                        }
                    });
                }

                // Elabora assenze - separa FORMAZIONE dalle altre
                if (dayAbsences.length > 0) {
                    dayAbsences.forEach(absence => {
                        const absenceTypeLower = (absence.absenceType || '').toLowerCase();
                        const isFormazione = absenceTypeLower.includes('formazione');

                        const absStart = moment(absence.start);
                        const absEnd = moment(absence.end);
                        let absenceHoursForDay = 8;

                        if (absStart.isSame(absEnd, 'day')) {
                            absenceHoursForDay = absEnd.diff(absStart, 'hours', true);
                        } else {
                            const currentDay = moment(day);
                            if (currentDay.isSame(absStart, 'day')) {
                                const dayEnd = currentDay.clone().hour(16).minute(0);
                                absenceHoursForDay = Math.max(0, Math.min(8, dayEnd.diff(absStart, 'hours', true)));
                            } else if (currentDay.isSame(absEnd, 'day')) {
                                const dayStart = currentDay.clone().hour(8).minute(0);
                                absenceHoursForDay = Math.max(0, Math.min(8, absEnd.diff(dayStart, 'hours', true)));
                            }
                        }

                        const wholeHours = Math.floor(absenceHoursForDay);
                        const remainingMinutes = Math.round((absenceHoursForDay - wholeHours) * 60);

                        if (isFormazione) {
                            workHours += wholeHours;
                            workMinutes += remainingMinutes;
                        } else {
                            absenceHours += wholeHours;
                            absenceMinutes += remainingMinutes;
                        }

                        let absenceLabel = 'Assenza';
                        if (isFormazione) {
                            absenceLabel = 'Formazione';
                        } else if (absenceTypeLower.includes('ferie') || absenceTypeLower.includes('feria')) {
                            absenceLabel = 'Ferie';
                        } else if (absenceTypeLower.includes('malattia')) {
                            absenceLabel = 'Malattia';
                        }

                        if (!workType) {
                            workType = isFormazione ? 'Lavorate' : absenceLabel;
                        } else if (workType !== absenceLabel && workType !== 'Misto') {
                            workType = 'Misto';
                        }

                        let timeStart, timeEnd;
                        if (absStart.isSame(absEnd, 'day')) {
                            timeStart = absStart.format('HH:mm');
                            timeEnd = absEnd.format('HH:mm');
                        } else {
                            const currentDay = moment(day);
                            if (currentDay.isSame(absStart, 'day')) {
                                timeStart = absStart.format('HH:mm');
                                timeEnd = '16:00';
                            } else if (currentDay.isSame(absEnd, 'day')) {
                                timeStart = '08:00';
                                timeEnd = absEnd.format('HH:mm');
                            } else {
                                timeStart = '08:00';
                                timeEnd = '16:00';
                            }
                        }

                        if (isFormazione) {
                            const hoursLabel = absenceHoursForDay < 8 ? ` (${absenceHoursForDay.toFixed(1)}h)` : '';
                            descriptions.push(`Formazione${hoursLabel}`);
                        } else {
                            absenceDetails.push(`${absence.absenceType || 'Assenza'} ${timeStart}-${timeEnd}`);
                            const hoursLabel = absenceHoursForDay < 8 ? ` (${absenceHoursForDay.toFixed(1)}h)` : '';
                            descriptions.push(`${absence.absenceType || 'Assenza'}${hoursLabel}`);
                        }
                    });
                }

                // Normalizza i minuti
                if (workMinutes >= 60) {
                    workHours += Math.floor(workMinutes / 60);
                    workMinutes = workMinutes % 60;
                }
                if (absenceMinutes >= 60) {
                    absenceHours += Math.floor(absenceMinutes / 60);
                    absenceMinutes = absenceMinutes % 60;
                }
                if (overtimeMinutes >= 60) {
                    overtimeHours += Math.floor(overtimeMinutes / 60);
                    overtimeMinutes = overtimeMinutes % 60;
                }

                // Calcolo straordinario
                let displayWorkHours = workHours;
                let displayWorkMinutes = workMinutes;

                if (holidayFlag) {
                    // Giorno festivo: tutte le ore lavorate → straordinario
                    overtimeHours += workHours;
                    overtimeMinutes += workMinutes;
                    displayWorkHours = 0;
                    displayWorkMinutes = 0;
                    if (overtimeMinutes >= 60) {
                        overtimeHours += Math.floor(overtimeMinutes / 60);
                        overtimeMinutes = overtimeMinutes % 60;
                    }
                } else {
                    // Giorno lavorativo: soglia = 8h - ore assenze
                    const totalWorkInMinutes = workHours * 60 + workMinutes;
                    const totalAbsenceInMinutes = absenceHours * 60 + absenceMinutes;
                    const regularCapMinutes = Math.max(0, 8 * 60 - totalAbsenceInMinutes);

                    if (totalWorkInMinutes > regularCapMinutes) {
                        const excessMinutes = totalWorkInMinutes - regularCapMinutes;
                        displayWorkHours = Math.floor(regularCapMinutes / 60);
                        displayWorkMinutes = regularCapMinutes % 60;
                        overtimeHours += Math.floor(excessMinutes / 60);
                        overtimeMinutes += excessMinutes % 60;
                        if (overtimeMinutes >= 60) {
                            overtimeHours += Math.floor(overtimeMinutes / 60);
                            overtimeMinutes = overtimeMinutes % 60;
                        }
                    }
                }

                // Costruisci stringa assenze
                let absencesDisplay = '-';
                if (absenceHours > 0 || absenceMinutes > 0) {
                    const totalAbsenceTime = formatHours(absenceHours, absenceMinutes);
                    if (absenceDetails.length > 0) {
                        absencesDisplay = `${totalAbsenceTime}<br/><small>${absenceDetails.join('; ')}</small>`;
                    } else {
                        absencesDisplay = totalAbsenceTime;
                    }
                }

                // Accumula i totali delle colonne
                totalWorkHours += displayWorkHours;
                totalWorkMinutes += displayWorkMinutes;
                totalAbsenceHours += absenceHours;
                totalAbsenceMinutes += absenceMinutes;
                totalOvertimeHours += overtimeHours;
                totalOvertimeMinutes += overtimeMinutes;

                return {
                    date: dateFormatted,
                    isHoliday: holidayFlag,
                    description: descriptions.length > 0 ? descriptions.join('; ') : '-',
                    workType: workType || (hasMissingHours ? 'Da inserire' : '-'),
                    hasMissingHours,
                    hours: displayWorkHours > 0 || displayWorkMinutes > 0 ? formatHours(displayWorkHours, displayWorkMinutes) : '-',
                    absences: absencesDisplay,
                    overtime: overtimeHours > 0 || overtimeMinutes > 0 ? formatHours(overtimeHours, overtimeMinutes) : '-'
                };
            });

            // Normalizza i minuti totali
            if (totalWorkMinutes >= 60) {
                totalWorkHours += Math.floor(totalWorkMinutes / 60);
                totalWorkMinutes = totalWorkMinutes % 60;
            }
            if (totalAbsenceMinutes >= 60) {
                totalAbsenceHours += Math.floor(totalAbsenceMinutes / 60);
                totalAbsenceMinutes = totalAbsenceMinutes % 60;
            }
            if (totalOvertimeMinutes >= 60) {
                totalOvertimeHours += Math.floor(totalOvertimeMinutes / 60);
                totalOvertimeMinutes = totalOvertimeMinutes % 60;
            }

            // Calcola i totali per tipo
            const totalsByType = {};
            Object.keys(employee.hoursByType || {}).forEach(type => {
                const data = employee.hoursByType[type];
                if (data.hours > 0 || data.minutes > 0) {
                    const typeLabels = {
                        lavorate: 'Lavorate',
                        ferie: 'Ferie',
                        assenze: 'Assenze',
                        malattia: 'Malattia',
                        altro: 'Altro'
                    };
                    totalsByType[typeLabels[type] || type] = formatHours(data.hours, data.minutes);
                }
            });

            // Creazione dell'HTML per la stampa
            const formattedStartDate = moment(startDate).format('DD/MM/YYYY');
            const formattedEndDate = moment(endDate).format('DD/MM/YYYY');
            const title = `Foglio ore di ${employee.userLabel}`;
            const subtitle = `Periodo: ${formattedStartDate} - ${formattedEndDate}`;
            
            // Recupero logo dell'account (stesso della sidebar)
            const accountLogo = accountData?.settings?.UI?.logo || '';
            
            // Costruzione dell'HTML
            const htmlContent = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="UTF-8">
                    <title>${title}</title>
                    <style>
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        
                        body { 
                            font-family: Arial, sans-serif; 
                            background-color: white;
                            color: #000;
                            padding: 20px;
                            font-size: 11px;
                        }
                        
                        .logo-container { 
                            text-align: center; 
                            margin-bottom: 20px; 
                        }
                        
                        .logo { 
                            max-height: 60px; 
                            max-width: 200px;
                        }
                        
                        h1 { 
                            color: #000; 
                            font-size: 18px; 
                            font-weight: bold;
                            text-align: center;
                            margin: 10px 0 5px 0;
                        }
                        
                        .subtitle {
                            text-align: center;
                            font-size: 12px;
                            color: #666;
                            margin-bottom: 15px;
                        }
                        
                        .summary { 
                            display: flex;
                            justify-content: center;
                            gap: 20px;
                            flex-wrap: wrap;
                            margin-bottom: 15px;
                            padding: 10px;
                            background-color: #f9f9f9;
                            border-radius: 4px;
                        }
                        
                        .summary-item { 
                            font-size: 12px;
                            font-weight: 600;
                            color: #000;
                        }
                        
                        .summary-item span {
                            background-color: #e0e0e0;
                            padding: 2px 8px;
                            border-radius: 3px;
                            margin-left: 5px;
                        }
                        
                        table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            margin-top: 10px; 
                        }
                        
                        th, td { 
                            border: 1px solid #000; 
                            padding: 6px 8px; 
                            text-align: left;
                            color: #000;
                        }
                        
                        th { 
                            background-color: #f5f5f5;
                            font-weight: bold;
                            font-size: 10px;
                        }
                        
                        td {
                            font-size: 10px;
                        }
                        
                        .day-name {
                            font-weight: 500;
                            color: #333;
                        }
                        
                        .empty-day {
                            color: #999;
                        }
                        
                        .holiday-row {
                            background-color: #f5f5f5;
                        }

                        .missing-hours-row {
                            background-color: #fff3cd;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                            color: #000;
                        }
                        
                        .totals-row {
                            background-color: #f0f0f0;
                            font-weight: bold;
                        }
                        
                        /* Stili per la stampa */
                        @media print {
                            body {
                                padding: 10px;
                                margin: 0;
                                background-color: white;
                            }
                            
                            th {
                                background-color: #f5f5f5 !important;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            
                            .totals-row {
                                background-color: #f0f0f0 !important;
                                -webkit-print-color-adjust: exact;
                                print-color-adjust: exact;
                            }
                            
                            table {
                                page-break-inside: auto;
                            }
                            
                            tr {
                                page-break-inside: avoid;
                                page-break-after: auto;
                            }
                        }
                    </style>
                </head>
                <body>
                    ${accountLogo ? `
                    <div class="logo-container">
                        <img src="${accountLogo}" class="logo" alt="Logo"/>
                    </div>
                    ` : ''}
                    
                    <h1>${title}</h1>
                    <div class="subtitle">${subtitle}</div>
                    
                    <div class="summary">
                        <div class="summary-item">Totale: <span>${formatHours(employee.totalHours, employee.totalMinutes)}</span></div>
                        ${Object.entries(totalsByType).map(([type, hours]) => `
                            <div class="summary-item">${type}: <span>${hours}</span></div>
                        `).join('')}
                        ${(employee.totalOvertimeHours > 0 || employee.totalOvertimeMinutes > 0) ? `
                            <div class="summary-item">Straordinario: <span>${formatHours(employee.totalOvertimeHours, employee.totalOvertimeMinutes)}</span></div>
                        ` : ''}
                        ${employee.missingHoursCount > 0 ? `
                            <div class="summary-item">Ore non inserite: <span>${employee.missingHoursCount}</span></div>
                        ` : ''}
                    </div>
                    
                    <table>
                        <thead>
                            <tr>
                                <th style="width: 80px;">Data</th>
                                <th>Descrizione</th>
                                <th style="width: 80px;">Tipo Ore</th>
                                <th style="width: 60px;">Ore</th>
                                <th style="width: 60px;">Assenze</th>
                                <th style="width: 70px;">Straord.</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${dayRows.map(row => `
                                <tr class="${row.isHoliday ? 'holiday-row' : ''} ${row.hasMissingHours ? 'missing-hours-row' : ''} ${!row.hasMissingHours && row.hours === '-' && row.absences === '-' ? 'empty-day' : ''}">
                                    <td>${row.date}</td>
                                    <td>${row.description}</td>
                                    <td>${row.workType}</td>
                                    <td>${row.hours}</td>
                                    <td>${row.absences}</td>
                                    <td>${row.overtime}</td>
                                </tr>
                            `).join('')}
                            <tr class="totals-row">
                                <td colspan="3" style="text-align: right;">TOTALE</td>
                                <td>${formatHours(totalWorkHours, totalWorkMinutes)}</td>
                                <td>${formatHours(totalAbsenceHours, totalAbsenceMinutes)}</td>
                                <td>${formatHours(totalOvertimeHours, totalOvertimeMinutes)}</td>
                            </tr>
                        </tbody>
                    </table>
                    
                    <script>
                        // Stampa automaticamente quando la pagina è caricata
                        window.onload = function() {
                            window.print();
                        }
                    </script>
                </body>
                </html>
            `;
            
            // Apri una nuova finestra con il contenuto da stampare
            const printWindow = window.open('', '_blank', 'width=800,height=600');
            if (printWindow) {
                printWindow.document.write(htmlContent);
                printWindow.document.close();
            }
            
        } catch (error) {
            console.error('Errore durante la stampa del foglio ore:', error);
            alert('Si è verificato un errore durante la preparazione della stampa');
        }
    };

    // Handlers per i filtri
    const handleEmployeeChange = (selected) => {
        if (selected) {
            setEmployeeId(selected.value);
            setEmployeeLabel(selected.label);
        } else {
            setEmployeeId('');
            setEmployeeLabel('');
        }
    };

    const handleCustomerChange = (selected) => {
        if (selected) {
            setCustomerId(selected.value);
            setCustomerLabel(selected.label);
        } else {
            setCustomerId('');
            setCustomerLabel('');
        }
    };

    const handleJobChange = (selected) => {
        if (selected) {
            setJobId(selected.value);
            setJobLabel(selected.label);
        } else {
            setJobId('');
            setJobLabel('');
        }
    };

    // Funzione per determinare se il file è un'immagine
    const isImageFile = (file) => {
        if (!file) return false;
        // Se è un oggetto con url
        if (typeof file === 'object' && file.url) {
            const url = file.url.toLowerCase();
            // Estrai l'estensione dall'URL
            const urlParts = url.split('/');
            const fileName = urlParts[urlParts.length - 1].split('?')[0];
            const ext = fileName.split('.').pop().toLowerCase();
            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
        }
        // Se è una stringa o altro formato
        if (typeof file === 'string' || (file && file.fileName)) {
            const fileName = typeof file === 'string' ? file : (file.fileName || '');
            const ext = fileName.split('.').pop().toLowerCase();
            return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext);
        }
        return false;
    };
    
    // Funzione per ottenere l'icona in base al tipo di file
    const getFileTypeIcon = (file) => {
        if (!file) return faFile;
        let fileRef = file.fileName || file.name || file.path || '';
        
        // Se abbiamo un url, estraiamo il nome file da esso
        if (!fileRef && file.url) {
            const urlParts = file.url.split('/');
            fileRef = urlParts[urlParts.length - 1] || '';
        }
        
        const ext = fileRef.split('.').pop().toLowerCase();
        
        const iconMap = {
            // Documenti
            'pdf': faFilePdf,
            'doc': faFileWord,
            'docx': faFileWord,
            'xls': faFileExcel,
            'xlsx': faFileExcel,
            'ppt': faFilePowerpoint,
            'pptx': faFilePowerpoint,
            'txt': faFileLines,
            // Immagini
            'jpg': faFileImage,
            'jpeg': faFileImage,
            'png': faFileImage,
            'gif': faFileImage,
            'bmp': faFileImage,
            'svg': faFileImage,
            'webp': faFileImage,
            // Archivi
            'zip': faFileZipper,
            'rar': faFileZipper,
            '7z': faFileZipper,
            'tar': faFileZipper,
            'gz': faFileZipper,
            // Audio/Video
            'mp3': faFileAudio,
            'wav': faFileAudio,
            'mp4': faFileVideo,
            'avi': faFileVideo,
            'mov': faFileVideo,
            'wmv': faFileVideo,
        };
        
        return iconMap[ext] || faFile;
    };
    
    // Funzione per ottenere l'URL dell'immagine
    const getImageUrl = (file) => {
        if (file.url) {
            return file.url;
        } else if (file.fileUrl) {
            return file.fileUrl;
        }
        return `${import.meta.env.VITE_API_BASE_URL || ''}/uploads/${file.path || file.filename || file.fileName || file.name}`;
    };
    
    // Funzione per aprire il file in una nuova tab
    const openFileInNewTab = (file) => {
        // Se file è un oggetto con url
        if (typeof file === 'object' && file.url) {
            window.open(file.url, '_blank');
            return;
        }

        // Altrimenti costruiamo l'URL del file
        const fileUrl = getImageUrl(file);
        window.open(fileUrl, '_blank');
    };
    
    // Funzione per scaricare tutti i file di un appuntamento
    const downloadAllFiles = async (appointment, closingDetail) => {
        // ID univoco per questo gruppo di file
        const downloadId = `${appointment._id}_${closingDetail.userId}`;
        
        // Imposta lo stato di download
        setDownloadingFiles(prev => ({ ...prev, [downloadId]: true }));
        
        try {
            if (!closingDetail.files || closingDetail.files.length === 0) {
                console.error('Nessun file disponibile per il download');
                return;
            }
            
            // Apri ogni file in una nuova scheda con un piccolo ritardo tra uno e l'altro
            for (let i = 0; i < closingDetail.files.length; i++) {
                const file = closingDetail.files[i];
                setTimeout(() => {
                    // Utilizziamo la stessa logica di openFileInNewTab
                    if (typeof file === 'object' && file.url) {
                        window.open(file.url, '_blank');
                    } else {
                        window.open(getImageUrl(file), '_blank');
                    }
                }, i * 300); // 300ms di ritardo tra un file e l'altro per evitare il blocco popup
            }
        } catch (err) {
            console.error('Errore nel download dei file:', err);
        } finally {
            // Rimuovi lo stato di download dopo un tempo appropriato
            const totalDelay = closingDetail.files ? closingDetail.files.length * 300 + 500 : 1000;
            setTimeout(() => {
                setDownloadingFiles(prev => ({ ...prev, [downloadId]: false }));
            }, totalDelay);
        }
    };
    
    // Componente per il dettaglio dell'appuntamento
    const AppointmentDetailCard = ({ appointment }) => {
        const closingDetail = appointment.closingDetail;
        const colors = useColorPalette();
        
        if (!closingDetail) return null;
        
        return (
            <Card className="mb-3 shadow-sm">
                <CardHeader className="d-flex justify-content-between">
                    <div>
                        <strong>{moment(appointment.fromDate).format('DD/MM/YYYY HH:mm')}</strong> - 
                        Commessa: {appointment.jobCode || 'N/D'}
                    </div>
                    <div>
                        {closingDetail.missingHours ? (
                            <span className="badge" style={{ backgroundColor: '#ffc107', color: 'black', padding: '6px 10px' }}>
                                Ore non inserite
                            </span>
                        ) : (
                            <>Ore: <strong>{formatHours(closingDetail.workHours || 0, closingDetail.workMinutes || 0)}</strong></>
                        )}
                    </div>
                </CardHeader>
                <CardBody>
                    <Row>
                        <Col md={6}>
                            <p><strong>Cliente:</strong> {appointment.customerLabel || closingDetail.customerLabel || 'N/D'}</p>
                            <p><strong>Impianto:</strong> {appointment.systemLabel || 'N/D'}</p>
                            <p><strong>Indirizzo:</strong> {appointment.systemAddressLabel || 'N/D'}</p>
                        </Col>
                        <Col md={6}>
                            <p><strong>Distanza Km:</strong> {parseInt(appointment.distanceKm) || 0} km</p>
                            <p><strong>Tempo di percorrenza:</strong> {appointment.distanceTime ? parseInt(appointment.distanceTime / 60) : 0} min</p>
                            <p><strong>Veicolo:</strong> {closingDetail.vehicleLabel || 'Nessun veicolo'}</p>
                        </Col>
                    </Row>
                    
                    <div className="mt-2">
                        <strong>Tipo commessa:</strong> {
                            (() => {
                                let label = appointment.jobTypeLabel || 'N/D';
                                if (appointment.assistanceRequestCode) {
                                    label += ` - Ass: ${appointment.assistanceRequestCode}`;
                                } else if (appointment.maintenanceCode) {
                                    label += ` - Man: ${appointment.maintenanceCode}`;
                                    if (appointment.maintenanceActionLabel) {
                                        label += ` (${appointment.maintenanceActionLabel})`;
                                    }
                                }
                                return label;
                            })()
                        }
                    </div>
                    
                    {closingDetail.notes && (
                        <div className="mt-2">
                            <strong>Note:</strong> {closingDetail.notes}
                        </div>
                    )}
                    
                    {/* Visualizzazione degli allegati usando il componente AttachmentsViewer */}
                    {closingDetail.files && closingDetail.files.length > 0 ? (
                        <div className="mt-3">
                            <AttachmentsViewer 
                                attachments={closingDetail.files} 
                                title={`Allegati (${closingDetail.files.length})`}
                                showPreview={true}
                                downloadAllHandler={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    downloadAllFiles(appointment, closingDetail);
                                }}
                                downloadInProgress={downloadingFiles[`${appointment._id}_${closingDetail.userId}`]}
                            />
                        </div>
                    ) : (
                        <div className="mt-2">
                            <p className="text-muted fst-italic">Nessun allegato disponibile</p>
                        </div>
                    )}
                    
                    <div className="mt-2 text-muted">
                        <small>
                            {closingDetail.missingHours
                                ? 'Il collaboratore non ha ancora chiuso l\'appuntamento: ore da verificare'
                                : `Registrato: ${moment(closingDetail.timestamp).format('DD/MM/YYYY HH:mm')}`}
                        </small>
                    </div>
                </CardBody>
            </Card>
        );
    };

    return (
        <Container fluid>
            <DynamicTitle title="Consuntivi Ore" />
            <h2 className="mb-4 text-dark font-weight-bold">Consuntivi Ore</h2>
            
            <Card className="mb-4" style={{ borderColor: colors.secondary }}>
                <CardHeader style={{ backgroundColor: colors.secondary, color: colors.textOnSecondary }}>
                    <h5 className="mb-0">Filtri</h5>
                </CardHeader>
                <CardBody>
                    <Row className="d-flex align-items-end">
                        {/* Periodo (date) */}
                        <Col lg={2} md={4} sm={6} xs={12} className="pe-md-1">
                            <FormGroup className="mb-0">
                                <Label for="startDate" className="small">Da</Label>
                                <Input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    bsSize="sm"
                                    style={{ height: '38px' }}
                                />
                            </FormGroup>
                        </Col>
                        <Col lg={2} md={4} sm={6} xs={12} className="ps-md-1 pe-md-1">
                            <FormGroup className="mb-0">
                                <Label for="endDate" className="small">A</Label>
                                <Input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    bsSize="sm"
                                    style={{ height: '38px' }}
                                />
                            </FormGroup>
                        </Col>
                        
                        {/* Select collaboratore */}
                        <Col lg={2} md={4} sm={6} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="employeeSelect" className="small">Collaboratore</Label>
                                <EmployeeSelect
                                    selectedOptions={employeeId && employeeLabel ? { value: employeeId, label: employeeLabel } : null}
                                    setSelectedOptions={handleEmployeeChange}
                                    isMulti={false}
                                    placeholder="Collaboratore..."
                                />
                            </FormGroup>
                        </Col>
                        
                        {/* Select cliente */}
                        <Col lg={3} md={6} sm={12} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="customerSelect" className="small">Cliente</Label>
                                <CustomerSelect
                                    value={customerId && customerLabel ? { value: customerId, label: customerLabel } : null}
                                    onChange={handleCustomerChange}
                                />
                            </FormGroup>
                        </Col>
                        
                        {/* Select commessa */}
                        <Col lg={3} md={6} sm={12} xs={12}>
                            <FormGroup className="mb-0">
                                <Label for="jobSelect" className="small">Commessa</Label>
                                <JobSelect
                                    selectedOption={jobId && jobLabel ? { value: jobId, label: jobLabel } : null}
                                    setSelectedOption={handleJobChange}
                                    isMulti={false}
                                    placeholder="Commessa..."
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </CardBody>
            </Card>
            
            {loading ? (
                <div className="text-center p-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Caricamento dati...</p>
                </div>
            ) : (
                <>
                    {/* Riepilogo totale ore */}
                    <Card className="mb-4" style={{ borderColor: colors.primary }}>
                        <CardHeader style={{ backgroundColor: colors.primary, color: colors.textOnPrimary }}>
                            <h5 className="mb-0">Riepilogo Ore</h5>
                        </CardHeader>
                        <CardBody>
                            <div className="text-center">
                                <h3>Totale ore nel periodo: {formatHours(hoursSummary.totalHours, hoursSummary.totalMinutes)}</h3>
                                {(hoursSummary.totalOvertimeHours > 0 || hoursSummary.totalOvertimeMinutes > 0) && (
                                    <h5 className="text-muted">di cui Straordinario: {formatHours(hoursSummary.totalOvertimeHours, hoursSummary.totalOvertimeMinutes)}</h5>
                                )}
                                <p className="text-muted">
                                    Periodo: {moment(startDate).format('DD/MM/YYYY')} - {moment(endDate).format('DD/MM/YYYY')}
                                </p>
                            </div>
                            
                            {/* Nuova riga con riepilogo ore per collaboratore e grafico a torta */}
                            {hoursSummary.employeeSummary && hoursSummary.employeeSummary.length > 0 && (
                                <Row className="mt-4">
                                    <Col md={6}>
                                        <h5 style={{ color: colors.primary }} className="mb-3 d-flex justify-content-between align-items-center">
                                            <span>Ore per Collaboratore</span>
                                            <FontAwesomeIcon 
                                                icon={faFilePdf} 
                                                onClick={generateAllEmployeeTimesheets}
                                                style={{ color: colors.primary, cursor: 'pointer', fontSize: '1.5rem' }} 
                                                title="Fogli ore collaboratori"
                                            />
                                        </h5>
                                        <div>
                                            {hoursSummary.employeeSummary.map((employee, index) => {
                                                return (
                                                    <React.Fragment key={employee.userId}>
                                                        <div 
                                                            className="mb-2 p-2" 
                                                            style={{ 
                                                                fontSize: '16px', 
                                                                backgroundColor: index % 2 === 0 ? 'rgba(0,0,0,0.04)' : 'transparent',
                                                                borderRadius: '4px'
                                                            }}
                                                        >
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div>
                                                                    <span style={{ fontWeight: '500' }}>{employee.userLabel}</span>
                                                                    <br />
                                                                    <small 
                                                                        onClick={() => toggleEmployeeDetails(employee.userId)}
                                                                        style={{ 
                                                                            cursor: 'pointer', 
                                                                            textDecoration: 'underline',
                                                                            color: colors.primary
                                                                        }}
                                                                    >
                                                                        {employee.appointments.length} appuntamenti
                                                                    </small>
                                                                </div>
                                                                <div className="d-flex align-items-center">
                                                                    <FontAwesomeIcon 
                                                                        icon={faEye} 
                                                                        onClick={() => toggleEmployeeDetails(employee.userId)}
                                                                        style={{ 
                                                                            color: colors.secondary, 
                                                                            cursor: 'pointer',
                                                                            marginRight: '12px' 
                                                                        }}
                                                                        title="Visualizza dettaglio attività"
                                                                    />
                                                                    <FontAwesomeIcon 
                                                                        icon={faFilePdf}
                                                                        onClick={() => generateEmployeeTimesheet(employee)}
                                                                        style={{ 
                                                                            color: colors.secondary, 
                                                                            cursor: 'pointer',
                                                                            marginRight: '12px'
                                                                        }}
                                                                        title="Foglio ore"
                                                                    />
                                                                    <span className="badge" style={{ 
                                                                        backgroundColor: colors.accent, 
                                                                        color: 'white',
                                                                        fontSize: '16px',
                                                                        padding: '8px 12px'
                                                                    }}>
                                                                        {formatHours(employee.totalHours, employee.totalMinutes)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            {/* Dettaglio ore per tipo - mostra solo se ci sono ore > 0 */}
                                                            {employee.hoursByType && (
                                                                <div className="mt-2 d-flex flex-wrap gap-2">
                                                                    {employee.hoursByType.lavorate && (employee.hoursByType.lavorate.hours > 0 || employee.hoursByType.lavorate.minutes > 0) && (
                                                                        <span className="badge" style={{ backgroundColor: '#28a745', color: 'white', padding: '6px 10px', fontSize: '14px' }}>
                                                                            Lavorate: {formatHours(employee.hoursByType.lavorate.hours, employee.hoursByType.lavorate.minutes)}
                                                                        </span>
                                                                    )}
                                                                    {employee.hoursByType.ferie && (employee.hoursByType.ferie.hours > 0 || employee.hoursByType.ferie.minutes > 0) && (
                                                                        <span className="badge" style={{ backgroundColor: '#17a2b8', color: 'white', padding: '6px 10px', fontSize: '14px' }}>
                                                                            Ferie: {formatHours(employee.hoursByType.ferie.hours, employee.hoursByType.ferie.minutes)}
                                                                        </span>
                                                                    )}
                                                                    {employee.hoursByType.assenze && (employee.hoursByType.assenze.hours > 0 || employee.hoursByType.assenze.minutes > 0) && (
                                                                        <span className="badge" style={{ backgroundColor: '#ffc107', color: 'black', padding: '6px 10px', fontSize: '14px' }}>
                                                                            Assenze: {formatHours(employee.hoursByType.assenze.hours, employee.hoursByType.assenze.minutes)}
                                                                        </span>
                                                                    )}
                                                                    {employee.hoursByType.malattia && (employee.hoursByType.malattia.hours > 0 || employee.hoursByType.malattia.minutes > 0) && (
                                                                        <span className="badge" style={{ backgroundColor: '#dc3545', color: 'white', padding: '6px 10px', fontSize: '14px' }}>
                                                                            Malattia: {formatHours(employee.hoursByType.malattia.hours, employee.hoursByType.malattia.minutes)}
                                                                        </span>
                                                                    )}
                                                                    {employee.hoursByType.altro && (employee.hoursByType.altro.hours > 0 || employee.hoursByType.altro.minutes > 0) && (
                                                                        <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', padding: '6px 10px', fontSize: '14px' }}>
                                                                            Altro: {formatHours(employee.hoursByType.altro.hours, employee.hoursByType.altro.minutes)}
                                                                        </span>
                                                                    )}
                                                                    {(employee.totalOvertimeHours > 0 || employee.totalOvertimeMinutes > 0) && (
                                                                        <span className="badge" style={{ backgroundColor: '#fd7e14', color: 'white', padding: '6px 10px', fontSize: '14px' }}>
                                                                            Straordinario: {formatHours(employee.totalOvertimeHours, employee.totalOvertimeMinutes)}
                                                                        </span>
                                                                    )}
                                                                    {employee.missingHoursCount > 0 && (
                                                                        <span className="badge" style={{ backgroundColor: '#ffc107', color: 'black', padding: '6px 10px', fontSize: '14px' }}>
                                                                            Ore non inserite: {employee.missingHoursCount} {employee.missingHoursCount === 1 ? 'appuntamento' : 'appuntamenti'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    </Col>
                                    <Col md={6} className="d-flex justify-content-center align-items-center">
                                        <div style={{ width: '100%', height: '300px' }}>
                                            <Bar 
                                                data={{
                                                    labels: hoursSummary.employeeSummary.map(emp => emp.userLabel),
                                                    datasets: [
                                                        {
                                                            label: 'Ore lavorate',
                                                            data: hoursSummary.employeeSummary.map(emp => emp.totalHours + (emp.totalMinutes / 60)),
                                                            backgroundColor: colors.primary,
                                                            borderColor: colors.secondary,
                                                            borderWidth: 1,
                                                            hoverBackgroundColor: colors.accent,
                                                        },
                                                    ],
                                                }}
                                                options={{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    indexAxis: 'y', // Rende l'istogramma orizzontale
                                                    plugins: {
                                                        legend: {
                                                            position: 'top',
                                                            labels: {
                                                                color: colors.textOnBackground,
                                                                font: {
                                                                    weight: 'bold'
                                                                }
                                                            }
                                                        },
                                                        title: {
                                                            display: true,
                                                            text: 'Distribuzione Ore Lavorate',
                                                            color: colors.primary,
                                                            font: {
                                                                size: 16,
                                                                weight: 'bold'
                                                            }
                                                        }
                                                    },
                                                    scales: {
                                                        x: {
                                                            grid: {
                                                                color: 'rgba(0, 0, 0, 0.1)'
                                                            },
                                                            ticks: {
                                                                color: colors.textOnBackground
                                                            }
                                                        },
                                                        y: {
                                                            grid: {
                                                                display: false
                                                            },
                                                            ticks: {
                                                                color: colors.textOnBackground
                                                            }
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                            )}
                        </CardBody>
                    </Card>
                    
                    {/* Il pannello Ore per Collaboratore è stato rimosso perché integrato nella sezione precedente */}
                    
                    {/* Dettaglio attività del collaboratore selezionato */}
                    {expandedEmployee && hoursSummary.employeeSummary.find(emp => emp.userId === expandedEmployee) && (
                        <Card className="mb-4" style={{ borderColor: colors.secondary }}>
                            <CardHeader style={{ backgroundColor: colors.secondary, color: colors.textOnSecondary }} className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">Dettaglio attività di {hoursSummary.employeeSummary.find(emp => emp.userId === expandedEmployee).userLabel}</h5>
                                <div className="d-flex gap-2">
                                    <FontAwesomeIcon 
                                        icon={faToolbox} 
                                        onClick={() => setViewMode('cards')}
                                        style={{ 
                                            cursor: 'pointer', 
                                            opacity: viewMode === 'cards' ? 1 : 0.5,
                                            fontSize: '1.2rem'
                                        }}
                                        title="Chiusure"
                                    />
                                    <FontAwesomeIcon 
                                        icon={faCalendarAlt} 
                                        onClick={() => setViewMode('table')}
                                        style={{ 
                                            cursor: 'pointer', 
                                            opacity: viewMode === 'table' ? 1 : 0.5,
                                            fontSize: '1.2rem'
                                        }}
                                        title="Dettaglio giorni"
                                    />
                                </div>
                            </CardHeader>
                            <CardBody>
                                {/* Box riepilogo ore per tipo */}
                                {(() => {
                                    const employee = hoursSummary.employeeSummary.find(emp => emp.userId === expandedEmployee);
                                    return (
                                        <div className="mb-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
                                            <h6 className="mb-3" style={{ color: colors.primary, fontWeight: '600' }}>
                                                <FontAwesomeIcon icon={faClock} className="me-2" />
                                                Riepilogo Ore
                                            </h6>
                                            <div className="d-flex flex-wrap gap-3" style={{ fontSize: '14px' }}>
                                                {/* Totale ore */}
                                                <div className="d-flex align-items-center">
                                                    <span className="badge" style={{ backgroundColor: colors.accent, color: 'white', padding: '8px 12px', fontSize: '14px' }}>
                                                        Totale: {formatHours(employee.totalHours, employee.totalMinutes)}
                                                    </span>
                                                </div>
                                                {/* Ore lavorate */}
                                                {employee.hoursByType?.lavorate && (employee.hoursByType.lavorate.hours > 0 || employee.hoursByType.lavorate.minutes > 0) && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge" style={{ backgroundColor: '#28a745', color: 'white', padding: '8px 12px', fontSize: '14px' }}>
                                                            Lavorate: {formatHours(employee.hoursByType.lavorate.hours, employee.hoursByType.lavorate.minutes)}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Ferie */}
                                                {employee.hoursByType?.ferie && (employee.hoursByType.ferie.hours > 0 || employee.hoursByType.ferie.minutes > 0) && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge" style={{ backgroundColor: '#17a2b8', color: 'white', padding: '8px 12px', fontSize: '14px' }}>
                                                            Ferie: {formatHours(employee.hoursByType.ferie.hours, employee.hoursByType.ferie.minutes)}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Assenze */}
                                                {employee.hoursByType?.assenze && (employee.hoursByType.assenze.hours > 0 || employee.hoursByType.assenze.minutes > 0) && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge" style={{ backgroundColor: '#ffc107', color: 'black', padding: '8px 12px', fontSize: '14px' }}>
                                                            Assenze: {formatHours(employee.hoursByType.assenze.hours, employee.hoursByType.assenze.minutes)}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Malattia */}
                                                {employee.hoursByType?.malattia && (employee.hoursByType.malattia.hours > 0 || employee.hoursByType.malattia.minutes > 0) && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge" style={{ backgroundColor: '#dc3545', color: 'white', padding: '8px 12px', fontSize: '14px' }}>
                                                            Malattia: {formatHours(employee.hoursByType.malattia.hours, employee.hoursByType.malattia.minutes)}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Altro */}
                                                {employee.hoursByType?.altro && (employee.hoursByType.altro.hours > 0 || employee.hoursByType.altro.minutes > 0) && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', padding: '8px 12px', fontSize: '14px' }}>
                                                            Altro: {formatHours(employee.hoursByType.altro.hours, employee.hoursByType.altro.minutes)}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Straordinario */}
                                                {(employee.totalOvertimeHours > 0 || employee.totalOvertimeMinutes > 0) && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge" style={{ backgroundColor: '#fd7e14', color: 'white', padding: '8px 12px', fontSize: '14px' }}>
                                                            Straordinario: {formatHours(employee.totalOvertimeHours, employee.totalOvertimeMinutes)}
                                                        </span>
                                                    </div>
                                                )}
                                                {/* Appuntamenti senza ore inserite */}
                                                {employee.missingHoursCount > 0 && (
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge" style={{ backgroundColor: '#ffc107', color: 'black', padding: '8px 12px', fontSize: '14px' }}>
                                                            Ore non inserite: {employee.missingHoursCount} {employee.missingHoursCount === 1 ? 'appuntamento' : 'appuntamenti'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                                
                                {/* Vista Card o Vista Tabella in base a viewMode */}
                                {viewMode === 'cards' ? (
                                    <Row className="g-3">
                                        {hoursSummary.employeeSummary.find(emp => emp.userId === expandedEmployee).appointments
                                            .sort((a, b) => new Date(b.fromDate) - new Date(a.fromDate))
                                            .map((appointment, idx) => (
                                                <Col xs={12} md={6} key={`${appointment._id}-${idx}`}>
                                                    <AppointmentDetailCard appointment={appointment} />
                                                </Col>
                                            ))}
                                    </Row>
                                ) : (
                                    /* Vista Tabella */
                                    (() => {
                                        const employee = hoursSummary.employeeSummary.find(emp => emp.userId === expandedEmployee);
                                        const tableData = generateEmployeeTableData(employee);
                                        return (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover" style={{ fontSize: '14px' }}>
                                                    <thead style={{ backgroundColor: colors.primary, color: colors.textOnPrimary }}>
                                                        <tr>
                                                            <th style={{ width: '100px' }}>Data</th>
                                                            <th>Descrizione</th>
                                                            <th style={{ width: '100px' }}>Tipo Ore</th>
                                                            <th style={{ width: '80px' }}>Ore</th>
                                                            <th style={{ width: '80px' }}>Straord.</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {tableData.map((row, idx) => (
                                                            <tr key={idx} style={{
                                                                backgroundColor: row.hasMissingHours ? '#fff3cd' : (row.isEmpty ? '#f8f9fa' : 'white'),
                                                                color: (row.isEmpty && !row.hasMissingHours) ? '#999' : 'inherit'
                                                            }}>
                                                                <td>{row.date}</td>
                                                                <td>{row.description}</td>
                                                                <td>{row.workType}</td>
                                                                <td>{row.hours}</td>
                                                                <td>{row.overtime}</td>
                                                            </tr>
                                                        ))}
                                                        <tr style={{ backgroundColor: '#e9ecef', fontWeight: 'bold' }}>
                                                            <td colSpan="3" style={{ textAlign: 'right' }}>TOTALE</td>
                                                            <td>{formatHours(employee.totalHours, employee.totalMinutes)}</td>
                                                            <td>{formatHours(employee.totalOvertimeHours || 0, employee.totalOvertimeMinutes || 0)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        );
                                    })()
                                )}
                            </CardBody>
                        </Card>
                    )}
                </>
            )}
        </Container>
    );
}
